// ─────────────────────────────────────────────────────────────
// TERROR NPC ENGINE — REDESIGNED (sequential, one-card-per-write)
//
// Beb      — always acts, random (but LEGAL) decisions, no hesitation
// Champion — plays correctly, attacks every turn, manages Chao,
//            trains Primeras, resurrects when its board is empty
// Lord     — chess-level: reads board state, calculates kill
//            turns, pivots strategy, neutralizes threats, and now
//            wields the FULL arsenal: searches, deviancy, training,
//            U'hlti/Affinity, soul activations, rules-true revival
//
// CARD GAME RULE: One card placed/activated/played at a time.
// Every visible action = its own Firebase write + its own pause.
// This makes the NPC readable for the player and lets them
// respond with L3 Tomes, plan blocks, etc.
//
// ═══════════════ NPC AWAKENING WAVE (July 2026) ═══════════════
// The "make them feel alive" milestone. New capabilities, all
// mirroring the PLAYER-side Shatterfield handlers exactly:
//
//   • DEVIANCY — legal Deviant Transcends (class match, ilk differ,
//     free) and Deviant Ascends (any next-level being, cost =
//     deviantAscendCost(sharedTraits, targetLevel) Chao — mirror of
//     Shatterfield.handleAscend). Deviant status is permanent/sticky.
//   • LEGALITY FIX — placeOneEscendant used to allow ANY L1 into a
//     slot (R16 violation: class must match the Escling, both paths).
//     Now only legal placements are considered.
//   • GRIMOIRE SEARCH — mirror of handleGrimoireSearch: pay 2 (L1) /
//     4 (L2+) Chao-else-Anima to tutor a being of a chosen level.
//     CONSUMES the turn's draw, exactly like the player.
//   • LUXMOIRE SEARCH — mirror of handleLuxmoireSearch: 5 Chao to
//     tutor any Primera. Does NOT consume the draw.
//   • TRAINING — trainOnePrimera mirrors handleAttachPrimera:
//     class+ilk match, Primera level <= being level, one Primera per
//     level (R71), free attach (the chaoCost on a Primera is its
//     ACTIVATION fare, paid in activateOneSoulCard).
//   • U'HLTI + AFFINITY — attachOneSoulCard mirrors
//     handleAttachSoulCard: U'hlti owner-bound, Natural L3 only, one
//     each, Affinity requires the U'hlti first. The NPC can now
//     assemble a Supreme Being.
//   • SOUL ACTIVATIONS — activateOneSoulCard: a compact mirror of
//     Shatterfield.handleActivateSoulCard covering every Primera /
//     U'hlti / Affinity in the 12 starter decks, with per-effect
//     "is this useful right now" gates so Chao isn't wasted. One
//     activation per soul card per turn (self-imposed guard — the
//     player side has no cooldown gate yet; the NPC stays modest).
//   • RESURRECTION, RULES-TRUE — resurrectOne fully mirrors
//     handleResurrect + findResurrectSlot: R44 once per card instance
//     per Solrend (resurrectedCards, append-only), R45/R46 order
//     (L1 → empty slot; L2/L3 only over its same-chain lower level,
//     souling the outgoing being — the CHAIN FIX), R47 cost 2/4 paid
//     Chao-else-Anima, R48 souls preserved. The old version could
//     drop an L3 straight onto an Escling — illegal — and could
//     revive the same card forever.
//   • DEFENSIVE-STATE PARITY — doAttacks now respects every state
//     the player can set: being-level mustBeRuinedFirst (Quasar —
//     getRequiredTargetNpc mirrors getRequiredTarget: escling flag
//     FIRST, then the D'uhmdumn tome), intangible (attack passes
//     through, flag consumed), nullifyNextAttack (consumed),
//     reflectNextAttack (Fate Maker — reflects the attacker's Lyt
//     onto the NPC's OWN Anima; the NPC can LOSE to it), and Sweet
//     Dreams (u_xulan_lord on a player being — the NPC dims its own
//     Anima by 1 per Lyt Attack, exactly like the human does).
//   • EFFECTIVE STATS — effLytPure / effLyfPure mirror Shatterfield's
//     getEffectiveLytPure / getEffectiveLyfPure (Gauntlets bond,
//     Diamond Skin, Cosmic Casbah team aura, Premier Dragon auras,
//     Apocalypse Now). The NPC attacks with its buffs and respects
//     the player's.
//   • OFFENSIVE SOUL FLAGS — the NPC's own attacks now consume /
//     honor extraAttack, extraAttackAnimaOnly, dimAnimaOnAttack,
//     spectralClaws, invFangsBuff, canDirectAnima, animaRedirectAll.
//   • PROPHECY TRACKING PARITY — new Anima dims write
//     buildAnimaLossUpdate / buildAnimaGainUpdate trackers like the
//     player side does.
// ─────────────────────────────────────────────────────────────

import { updatePlayer, updateMatch, endMatch, getMatch } from "./gameManager";
import { requestWindow } from "./ResponseBridge";
import { buildAnimaLossUpdate, buildAnimaGainUpdate } from "./prophecyEngine";
import { ESCENDANTS, TOMES, PRIMERAS, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";

const ALL_CARDS = [...ESCENDANTS, ...TOMES, ...PRIMERAS, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const getCard = (id) => ALL_CARDS.find(c => c.id === id) || null;

// ─────────────────────────────────────────────────────────────
// BUG #1 FIX (must-ruin-first) — universal-rules parity.
//
// The player-side enforcement (Shatterfield.getRequiredTarget) already
// blocks the human from attacking past a face-up Chao D'uhmdumn. The NPC
// had NO equivalent awareness, so when the PLAYER dropped a D'uhmdumn the
// NPC attacked straight past it — reading in-game as "the rule isn't
// blocking." This mirrors Shatterfield's self-deriving design EXACTLY:
// the requirement is intrinsic to card identity, never a stored flag.
//
// Chao D'uhmdumn is a TOME with Lyf (0/1/1) — a TAUNT WALL. Canon per
// caP'n (July 2026): "every single attack must first be directed at the
// D'uhmdumn, whether Lyt attack, Tome, etc." So the NPC does NOT freeze
// its attack phase anymore — it DIRECTS its Lyt attacks at the tome:
//   • Lyt >= tome's current Lyf → the tome is ruined (to necropolis)
//   • Lyt <  current Lyf        → chip the tome's Lyf
//   • Lord's nullifyFirstRuin   → the FIRST hit (of any kind) is soaked;
//     the flag is consumed and the attack does nothing.
// Once the tome is gone, remaining attackers resume normal targeting
// within the SAME attack phase. See attackRuinFirstTome below.
const TOME_RUIN_FIRST_IDS = new Set([
  "tome_chao_duhmdumn_beb",
  "tome_chao_duhmdumn_champion",
  "tome_chao_duhmdumn_lord",
]);

// ── riftArray — Firebase array-hole normalizer (July 2026) ──
// RTDB deletes null entries and returns sparse integer-keyed OBJECTS
// for arrays like tomes [null, x, null] → {1:x}. Any `.findIndex`/
// `.some`/`.forEach` on that object crashes (or silently misses empty
// slots — a length-1 array from {0:x} hides slots 1 and 2). Coerce any
// shape into a fixed-length array with explicit nulls.
const riftArray = (v, len) => {
  const out = new Array(len).fill(null);
  if (Array.isArray(v)) {
    for (let i = 0; i < len; i++) out[i] = v[i] ?? null;
  } else if (v && typeof v === "object") {
    Object.keys(v).forEach(k => {
      const n = parseInt(k, 10);
      if (n >= 0 && n < len) out[n] = v[k];
    });
  }
  return out;
};

// Returns the first face-up priority-target tome on a field, or null.
// Face-down tomes are hidden and impose no block. Derived from card
// identity only — no reliance on a persisted mustBeRuinedFirst flag.
function getRuinFirstTome(field) {
  const tomes = riftArray(field?.tomes, 3);
  const idx = tomes.findIndex(
    t => t?.id && !t?.faceDown && TOME_RUIN_FIRST_IDS.has(t.id)
  );
  if (idx < 0) return null;
  const t = tomes[idx];
  return { slotIdx: idx, id: t.id, name: getCard(t.id)?.name || "this Tome" };
}

// ── AWAKENING: full mirror of Shatterfield.getRequiredTarget ──
// The player side checks the ESCLING-slot flag FIRST (Quasar-style —
// "Choose 1 of your beings, it must be ruined before your adversary can
// attack another being" — an activated state, legitimately flag-based),
// THEN the intrinsic D'uhmdumn tome requirement. The NPC only ever knew
// about the tome — a player Quasar taunt was silently ignored.
// Returns { kind: "escling"|"tome", slotIdx, id, name } or null.
function getRequiredTargetNpc(field) {
  if (!field) return null;
  const esclings = field.esclings || [];
  const esclingMatch = esclings.findIndex(s => s?.escendant && s?.mustBeRuinedFirst);
  if (esclingMatch >= 0) {
    const s = esclings[esclingMatch];
    return { kind: "escling", slotIdx: esclingMatch, id: s.escendant, name: getCard(s.escendant)?.name || "this being" };
  }
  const tome = getRuinFirstTome(field);
  if (tome) return { kind: "tome", ...tome };
  return null;
}

// ─────────────────────────────────────────────────────────────
// UNIVERSAL RUIN SEMANTICS — descendPlayerSlot (July 2026)
//
// THE fix for "soul entirely destroyed instead of descending."
// The NPC's three ruin sites used to write `escendant:null, soul:[]`
// — obliterating the player's being AND deleting its soul cards from
// the game (they never even reached the Necropolis). This helper
// mirrors Shatterfield.descendBeing EXACTLY, so a ruin caused by the
// NPC behaves identically to one caused by a human:
//
//   • L2/L3 with its lower level in the soul → DESCENDS one level.
//     Top escendant + its LEVEL-MATCHED Primeras go to Necropolis
//     (canon: "Primeras belong to their level counterpart" — the
//     ruined level's Primera ruins with it; lower-level Primeras,
//     lower Escendants, U'hltis and Affinities stay in the soul).
//     Descended slot is Fallen for one turn (fallenTurn = match turn).
//     One-shot flags (mustBeRuinedFirst / nullifyNextAttack /
//     reflectNextAttack) clear — they belonged to the ruined level.
//   • L1 (or no lower level in soul) → FULL RUIN: escendant + entire
//     soul go to Necropolis, slot resets to bare Escling, and any
//     Treasure bonded to the slot (wieldedBy) cascades to Necropolis.
//
// MUTATES liveField + liveNec in place (the doAttacks pattern);
// caller writes both via its own updatePlayer. Returns "descended"
// | "ruined" | null so callers can log/pace accurately.
// ─────────────────────────────────────────────────────────────
function descendPlayerSlot(liveField, liveNec, slotIdx, matchTurn) {
  const slot = liveField?.esclings?.[slotIdx];
  if (!slot?.escendant) return null;
  const topCard = getCard(slot.escendant);
  if (!topCard) return null;

  liveNec.push(slot.escendant);
  const soul = slot.soul || [];
  const prevLevelId = soul.find(id => {
    const c = getCard(id);
    return c?.type === "Escendant" && c?.level === (topCard.level - 1);
  });

  if (prevLevelId && topCard.level > 1) {
    // ── DESCENT ──
    const keptSoul = soul.filter(id => {
      const c = getCard(id);
      if (!c) return false;
      if (c.type === "Escendant") return true;
      // Level-bound Primeras: the ruined level's Primera ruins with it.
      if (c.type === "Primera") return (c.level ?? 0) !== topCard.level;
      if (c.type === "U'hlti" || c.type === "Affinity") return true;
      return false;
    });
    soul.filter(id => !keptSoul.includes(id)).forEach(id => liveNec.push(id));
    const prevCard = getCard(prevLevelId);
    liveField.esclings[slotIdx] = {
      ...slot,
      escendant: prevLevelId,
      currentLyf: prevCard?.lyf ?? null,
      soul: keptSoul.filter(id => id !== prevLevelId),
      attackedThisTurn: false,
      intangible: false,
      mustBeRuinedFirst: false,
      nullifyNextAttack: false,
      reflectNextAttack: false,
      fallen: true,
      fallenTurn: matchTurn || 0,
    };
    return "descended";
  }

  // ── FULL RUIN — soul to Necropolis, Treasures cascade ──
  soul.forEach(id => liveNec.push(id));
  liveField.esclings[slotIdx] = {
    ...slot, escendant: null, currentLyf: null, soul: [],
    fallen: false, fallenTurn: null, attackedThisTurn: false, intangible: false,
    mustBeRuinedFirst: false, nullifyNextAttack: false, reflectNextAttack: false,
  };
  liveField.tomes = riftArray(liveField.tomes, 3);
  liveField.tomes.forEach((t, i) => {
    if (t?.id && t?.wieldedBy === slotIdx) {
      liveNec.push(t.id);
      liveField.tomes[i] = null;
    }
  });
  return "ruined";
}

export const NPC_IDX = 1;
export const NPC_DIFFICULTIES = {
  BEB:      "Beb",
  CHAMPION: "Champion",
  LORD:     "Lord",
};

// ═════════════════════════════════════════════════════════════
// DEPTHS BRAIN (July 2026) — skill + temperament
// ═════════════════════════════════════════════════════════════
// Scaling is DECISION QUALITY ONLY. The NPC and the player follow the
// same rulebook with no exceptions (canon) — no stat cheats, no free
// resources, no illegal moves. A higher-skill NPC simply makes better
// choices inside the same rules; a temperament biases WHICH good
// choices it prefers, so two opponents at the same ELO play with
// different souls.
//
//   skill        0..1 scalar. Depths opponents derive it from ELO
//                (EndlessDepths.skillFromElo); gatekeepers carry an
//                authored value; everything outside the Depths falls
//                back to TIER_BASELINE_SKILL so Plaza / Story / Dungeon
//                behavior is unchanged by this wave (Beb 0.5 and
//                Champion 0.35 reproduce the pre-wave chance gates;
//                Lord 0.7 turns the prophecy brain ON everywhere —
//                that upgrade is deliberate).
//   temperament  "Reckless" | "Patient" | "Greedy" | "Cold" | absent.
//                Absent = Neutral (pre-wave behavior).
//
// SKILL GATES — where each unlock lives on the ladder:
//   ≥ 0.38  Champion: ascension-line Grimoire Search (top of the
//           Champion ELO band + all gatekeeper Champions)
//   ≥ 0.40  Champion: third soul activation per turn
//   ≥ 0.70  Lord: PROPHECY brain (race its own win condition)
//   ≥ 0.82  Lord: LETHAL FOCUS (a clear, lethal Anima lane is never
//           traded for a being kill — Aenarchist band)
//   ≥ 0.95  Lord: PROPHECY DENIAL (hunts the being that completes the
//           PLAYER's prophecy — Solrend band + Rük)
const SKILL_CHAMP_LINE_SEARCH = 0.38;
const SKILL_CHAMP_SOUL3       = 0.40;
const SKILL_PROPHECY          = 0.70;
const SKILL_LETHAL_FOCUS      = 0.82;
const SKILL_DENIAL            = 0.95;

export const NPC_TEMPERAMENTS = ["Reckless", "Patient", "Greedy", "Cold"];

// Temperament knobs — every field is WIRED, nothing decorative:
//   chaos       flat adjustment to every random-appetite gate
//   luxBias     extra pull toward Luxmoire draws (Primera hunger)
//   animaHunger Champion prefers redirecting to the Anima over chip
//               damage when trained for it
//   zeroRandom  collapses randomness — any appetite ≥ 0.5 becomes
//               certain, below never fires. The Cold ones don't dice.
const TEMPERAMENT_PROFILES = {
  Reckless: { chaos: +0.12, luxBias: 0,     animaHunger: true,  zeroRandom: false },
  Patient:  { chaos: -0.10, luxBias: 0,     animaHunger: false, zeroRandom: false },
  Greedy:   { chaos: 0,     luxBias: +0.25, animaHunger: false, zeroRandom: false },
  Cold:     { chaos: 0,     luxBias: 0,     animaHunger: true,  zeroRandom: true  },
  Neutral:  { chaos: 0,     luxBias: 0,     animaHunger: false, zeroRandom: false },
};

const TIER_BASELINE_SKILL = { Beb: 0.5, Champion: 0.35, Lord: 0.7 };

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));
const lerp = (a, b, k) => a + (b - a) * clamp01(k);
// One gate for every random appetite. Temperament chaos shifts the
// probability; zeroRandom temperaments make it deterministic.
const chance = (p, brain) => {
  const adj = clamp01(p + (brain?.t?.chaos || 0));
  if (brain?.t?.zeroRandom) return adj >= 0.5;
  return Math.random() < adj;
};

// ── One-shot brain handoff ──
// The launch chain is EndlessDepths.handleFight → Plaza onFight →
// App.handleEnterNPC → buildNPCPlayerData, and only the FIRST link
// knows the opponent's ELO/temperament. Rather than threading a new
// argument through three files, the launcher parks the brain here and
// buildNPCPlayerData consumes it (consume-once — a stale brain from an
// aborted launch can at worst mis-tune one match, never leak forever).
// Launchers that never call this get tier baselines: Plaza ranked/free,
// Story, Dungeon bosses, Inner Emperium all play exactly as before.
let _pendingNpcBrain = null;
export function setNextNpcBrain(brain) { _pendingNpcBrain = brain || null; }
function consumePendingNpcBrain() {
  const b = _pendingNpcBrain;
  _pendingNpcBrain = null;
  return b;
}

// Resolve the working brain from the seat-1 object (stamped at build
// time). Reading it off the match means runNPCTurn's signature — and
// App.js — never had to change.
function resolveBrain(npc, difficulty) {
  const skill = clamp01(
    (typeof npc?.npcSkill === "number") ? npc.npcSkill
      : (TIER_BASELINE_SKILL[difficulty] ?? 0.5)
  );
  const temperament = NPC_TEMPERAMENTS.includes(npc?.temperament)
    ? npc.temperament : "Neutral";
  return { skill, temperament, t: TEMPERAMENT_PROFILES[temperament] };
}

// ─────────────────────────────────────────────────────────────
// PACING — single source of truth for every visible delay.
// Tune here if anything feels too fast/slow.
// ─────────────────────────────────────────────────────────────
const ACTION_DELAY    = 750;   // every visible card placement / activation
const POST_RUIN_DELAY = 1200;  // after destroying player being (descent visibility)
const TOME_INTERRUPT  = 1100;  // after activating a tome (interrupt window for L3 response)
const RESURRECT_DELAY = 1300;  // dramatic resurrect from necropolis
const KILL_SHOT_DELAY = 800;   // direct anima attacks
const SEARCH_DELAY    = 950;   // Grimoire/Luxmoire searches (deliberate, readable)
const THINK_TIME = { Beb: 1400, Champion: 2000, Lord: 2800 };

// ─────────────────────────────────────────────────────────────
// COST GATE — canonical helpers (universal rules, mirrors player)
// ─────────────────────────────────────────────────────────────
// Both player and NPC follow the SAME rulebook. Player-side cost
// gating lives in Shatterfield.spendChao (line 2803). These helpers
// mirror that pattern exactly:
//   1. Read fresh from Firebase (no local state mirror to desync)
//   2. Gate atomically — return false if insufficient, no write
//   3. Deduct via single updatePlayer write
//
// USE THESE for every NPC cost path. Never inline `state.chao -= cost`
// or read a stale snapshot value as the gate. The previous pattern
// (passing a `state` object through helpers) caused Bug F — NPC fired
// Solomon Godfry's Manslaughter at 5 Chao when it cost 8, because the
// local mirror had drifted from the Firebase truth across a long
// sequential turn.
async function npcSpendChao(code, amount) {
  if (amount <= 0) return true; // free actions always succeed
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.chao ?? 0;
  if (cur < amount) return false;
  await updatePlayer(code, NPC_IDX, {
    chao: cur - amount,
    chaoSpentThisCycle: true,
  });
  return true;
}

async function npcSpendAnima(code, amount) {
  if (amount <= 0) return true;
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.anima ?? 0;
  if (cur < amount) return false;
  await updatePlayer(code, NPC_IDX, { anima: cur - amount });
  return true;
}

// ── AWAKENING: Chao-else-Anima payment (mirror of handleResurrect) ──
// The player's Resurrection and Grimoire Search costs are payable in
// either currency; the player-side pattern is spendChao first, and on
// failure deduct Anima (if it covers the cost). Same order here.
async function npcPayChaoElseAnima(code, amount) {
  if (amount <= 0) return true;
  if (await npcSpendChao(code, amount)) return true;
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.anima ?? 0;
  if (cur - amount < 0) return false;
  await updatePlayer(code, NPC_IDX, { anima: cur - amount });
  return true;
}

// Mirrors Shatterfield.isTomePersistent (line 5322). Persistent tomes
// stay on the field and can be re-activated; non-persistent ruin to
// necropolis after firing once.
const FIELD_PERSISTENT_TOMES = new Set([
  "tome_chao_duhmdumn_beb", "tome_chao_duhmdumn_champion", "tome_chao_duhmdumn_lord",
  "tome_lightning_blade", "tome_gauntlets_azka",
  "tome_necklace_akula",
  "tome_necklace_death", "tome_necklace_courage",
  "tome_restraining_ordeal", "tome_magma_carta", "tome_king_of_the_castle",
]);
function isTomePersistent(card) {
  if (!card) return false;
  const tag = card.effectTag;
  if (tag === "OPT" || tag === "OP2" || tag === "OP3") return true;
  if (tag === "ATV") return true;
  if (FIELD_PERSISTENT_TOMES.has(card.id)) return true;
  return false;
}

// Push a non-persistent tome to necropolis after it fires (Bug A fix).
// Mirrors player-side handlePlayTome lines 5372-5373.
async function ruinNpcTome(code, slotIdx, cardId) {
  await updatePlayer(code, NPC_IDX, { [`field/tomes/${slotIdx}`]: null });
  const m = await getMatch(code);
  const nec = [...((m?.players?.[NPC_IDX]?.necropolis) || []), cardId];
  await updatePlayer(code, NPC_IDX, { necropolis: nec });
}

// ─────────────────────────────────────────────────────────────
// PER-TOME EFFECT HELPERS — start of inline-effects pattern.
//
// Background: Shatterfield.applyTomeEffect (line 2665) is a React
// closure that captures playerIdx/oppIdx/me/opponent/setTargetPicker/
// showToast. It cannot be called from NPC.js. The NPC has historically
// fired tomes "free" — paying cost, ruining the tome, but applying no
// effect. Some tomes (Chao Garden, Bhodi Tree, etc.) had inline NPC
// implementations in activateOneTome; most did not.
//
// Pattern: each helper is a standalone async function taking only
// (code) — it reads fresh state from Firebase, mirrors the Shatterfield
// handler logic, and is callable from any NPC code path.
//
// As more "NPC fires tome but nothing happens" bugs surface, add
// helpers here. Eventually consolidate into a single applyNpcTomeEffect
// dispatcher that mirrors Shatterfield's full 1281-line dispatcher.
// ─────────────────────────────────────────────────────────────

// Solomon Godfry's Manslaughter — symmetric forced ruin (Bug G fix).
// Card text: "Both Warlocks must ruin 1 of their own beings and its Soul."
// Mirrors Shatterfield.applyTomeEffect lines 2933-2950.
//
// Target choice matches player-side behavior: first alive being on each
// side. This is intentionally not a "smart" pick — when the player
// activates Solomon, Shatterfield doesn't even prompt with a target
// picker (forced ruin), it just takes myAlive[0] and oppAlive[0].
// Symmetric NPC behavior keeps the card consistent.
async function applySolomonGodfryEffect(code) {
  const m = await getMatch(code);
  const npc    = m?.players?.[NPC_IDX] || {};
  const oppIdx = NPC_IDX === 1 ? 0 : 1;
  const opp    = m?.players?.[oppIdx] || {};

  // NPC's own being ruined (the cost — was missing entirely)
  const npcAlive = (npc.field?.esclings || [])
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s?.escendant);
  if (npcAlive.length > 0) {
    const { s, i } = npcAlive[0];
    const f = JSON.parse(JSON.stringify(npc.field || {}));
    // "Ruin 1 being AND its Soul" — full ruin is correct here, but the
    // soul cards go to NECROPOLIS (they used to vanish from the game).
    const nec = [...(npc.necropolis || []), s.escendant, ...(s.soul || [])];
    f.esclings[i] = { ...s, escendant: null, currentLyf: null, fallen: false, soul: [] };
    await updatePlayer(code, NPC_IDX, { field: f, necropolis: nec });
    await sleep(POST_RUIN_DELAY); // descent visibility on NPC's own slot
  }

  // Opponent (player) being ruined (the upside)
  const oppAlive = (opp.field?.esclings || [])
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s?.escendant);
  if (oppAlive.length > 0) {
    const { s, i } = oppAlive[0];
    const f = JSON.parse(JSON.stringify(opp.field || {}));
    // Soul rides to Necropolis with the being — never deleted from play.
    const nec = [...(opp.necropolis || []), s.escendant, ...(s.soul || [])];
    f.esclings[i] = { ...s, escendant: null, currentLyf: null, fallen: false, soul: [] };
    await updatePlayer(code, oppIdx, { field: f, necropolis: nec });
    await sleep(POST_RUIN_DELAY); // descent visibility on player's slot
  }
}

// ─────────────────────────────────────────────────────────────
// CORE HELPERS
// ─────────────────────────────────────────────────────────────
function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function drawFromPool(pool) {
  const cards = [];
  Object.entries(pool || {}).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) cards.push(id); });
  return cards.length ? pickRandom(cards) : null;
}

function drawAndDeplete(pool) {
  const card = drawFromPool(pool);
  if (!card) return { card: null, newPool: pool };
  const newPool = { ...pool };
  if (newPool[card] > 1) newPool[card]--; else delete newPool[card];
  return { card, newPool };
}

function canAscendNatural(currentCard, nextCard) {
  if (!currentCard || !nextCard) return false;
  return nextCard.type === "Escendant" &&
    nextCard.level === currentCard.level + 1 &&
    nextCard.class === currentCard.class &&
    nextCard.ilk === currentCard.ilk;
}

// ── AWAKENING: shared-trait mirrors (Shatterfield lines 260-278) ──
// sharedTraits counts Class + Ilk matches (0, 1, or 2). Two traits =
// natural (free); one trait costs the TARGET card's level; zero traits
// costs 2/3/5 by target level. Mirrors deviantAscendCost exactly.
function sharedTraits(cardA, cardB) {
  if (!cardA || !cardB) return 0;
  let traits = 0;
  if (cardA.class === cardB.class) traits++;
  if (cardA.ilk === cardB.ilk) traits++;
  return traits;
}

function deviantAscendCost(traits, level) {
  if (traits === 2) return 0; // natural — free
  if (traits === 1) return level; // 1 shared
  return level === 1 ? 2 : level === 2 ? 3 : 5; // 0 shared
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: EFFECTIVE STAT MIRRORS
// Full mirrors of Shatterfield.getEffectiveLytPure (line 198) and
// getEffectiveLyfPure (line 235). Pure functions of (slot, fields) —
// no React, no Firebase. The one intentional difference: the player
// version resolves the slot index by object identity (s === slot),
// which breaks after spreads; the NPC version takes slotIdx explicitly.
// If a new passive aura lands in Shatterfield, mirror it HERE too.
// ─────────────────────────────────────────────────────────────
function countBeingsOfIlk(field, ilk) {
  return (field?.esclings || []).filter(s => { const c = getCard(s?.escendant); return c && c.ilk === ilk; }).length;
}

function effLytPure(slot, slotIdx, myField, oppField = null) {
  const card = getCard(slot?.escendant);
  if (!card) return 0;
  let lyt = card.lyt || 0;
  // Gauntlets of A'zka bonded to this slot (BUG #2 FIX, preserved)
  const hasGauntletsBonded = riftArray(myField?.tomes, 3).some(t =>
    t?.id === "tome_gauntlets_azka" && t?.wieldedBy === slotIdx && !t?.faceDown
  );
  if (hasGauntletsBonded) lyt += 2;
  // Legacy soul-slot Gauntlets (pre-Wave-1 fallback, parity with Shatterfield)
  if ((slot?.soul || []).some(id => id === "tome_gauntlets_azka")) lyt += 2;
  // Premier Dragon Beb — +1 Lyt per Dragon Being (own field only)
  if (card.id === "premier_dragon_beb") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_1")) lyt += countBeingsOfIlk(myField, "Dragon");
  }
  // Premier Dragon Champion — +1 Lyt per Dragon Being (BOTH fields)
  if (card.id === "premier_dragon_champion") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_2")) {
      lyt += countBeingsOfIlk(myField, "Dragon");
      lyt += countBeingsOfIlk(oppField, "Dragon");
    }
  }
  // Z4Z Lord U'hlti Apocalypse Now — +1 Lyt per Time Being (BOTH fields)
  if ((slot?.soul || []).some(id => id === "u_z4z_lord")) {
    lyt += countBeingsOfIlk(myField, "Time");
    lyt += countBeingsOfIlk(oppField, "Time");
  }
  return lyt;
}

function effLyfPure(slot, myField) {
  const card = getCard(slot?.escendant);
  if (!card) return slot?.currentLyf ?? 0;
  let lyf = slot?.currentLyf !== null && slot?.currentLyf !== undefined ? slot.currentLyf : (card.lyf ?? 0);
  // Xulan Wanderer Diamond Skin — +2 Lyf passive
  if ((slot?.soul || []).some(id => id === "p_xulan_1")) lyf += 2;
  // Cosmic Casbah — TEAM AURA: every friendly being gains +1 Lyf per Astro Being
  const anyCosmicCasbah = (myField?.esclings || []).some(s =>
    (s?.soul || []).some(id => id === "p_h09_1")
  );
  if (anyCosmicCasbah) lyf += countBeingsOfIlk(myField, "Space");
  // Premier Dragon Beb — +1 Lyf per Dragon Being (when primera attached)
  if (card.id === "premier_dragon_beb") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_1")) lyf += countBeingsOfIlk(myField, "Dragon");
  }
  return lyf;
}

// Effective vulnerability — mirrors the player-side gate: a being is
// vulnerable (ruinable) only when its EFFECTIVE Lyf is 0, not its raw
// currentLyf. A Diamond Skin being at currentLyf 0 still shows Lyf 2.
function isVulnerable(slot, field) { return effLyfPure(slot, field) === 0; }

function playerAnimaClear(playerField) {
  return (playerField?.esclings || []).every(s => !s?.escendant || s?.fallen);
}

function standingBeings(field) {
  return (field?.esclings || [])
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => s?.escendant && !s?.fallen);
}

// ── AWAKENING: effective total Lyt — fields, not raw arrays ──
// Signature change: (field, oppField) instead of (esclings), so the
// Gauntlets bond / cross-field auras are counted in kill math.
function totalAvailableLyt(field, oppField = null) {
  return (field?.esclings || []).reduce((sum, slot, idx) => {
    if (!slot?.escendant || slot.attackedThisTurn || slot.fallen) return sum;
    return sum + effLytPure(slot, idx, field, oppField);
  }, 0);
}

// ── BUG #2 FIX: effective Lyt for an NPC being, including Gauntlets bond ──
// Now a thin wrapper over the full effLytPure mirror (AWAKENING).
// `field` is the NPC's own field; `slotIdx` is the attacker's escling
// slot; `oppField` (optional) enables the cross-field auras.
function getNpcEffectiveLyt(field, slotIdx, oppField = null) {
  const slot = field?.esclings?.[slotIdx];
  if (!slot?.escendant) return 0;
  return effLytPure(slot, slotIdx, field, oppField);
}

function highestLevel(esclings) {
  return esclings.reduce((max, s) => {
    const lv = getCard(s?.escendant)?.level || 0;
    return lv > max ? lv : max;
  }, 0);
}

function canAscendFromPool(slot, pool) {
  const cur = getCard(slot?.escendant);
  if (!cur || cur.level >= 3) return false;
  return Object.keys(pool || {}).some(id => {
    const c = getCard(id);
    return c && canAscendNatural(cur, c);
  });
}

function threatScore(player, playerField) {
  let score = 0;
  const beings = standingBeings(playerField);
  beings.forEach(({ s, idx }) => {
    const c = getCard(s.escendant);
    if (!c) return;
    score += effLytPure(s, idx, playerField) * 2;
    score += (c.level || 1) * 5;
    if (c.level === 3) score += 15;
    if ((s.soul || []).length >= 4) score += 20;
  });
  score += Math.max(0, 30 - (player.anima || 30));
  return score;
}

function turnsUntilDeath(npcAnima, playerField, npcField = null) {
  const lyt = totalAvailableLyt(playerField, npcField);
  if (lyt <= 0) return 99;
  return Math.ceil(npcAnima / lyt);
}

// ─────────────────────────────────────────────────────────────
// TOME CATEGORIES
// ─────────────────────────────────────────────────────────────
const GOOD_TOMES = [
  "tome_chao_garden","tome_chao_arcadia","tome_chao_paradise",
  "tome_bhodi_tree","tome_anima_house","tome_stars_and_skies",
];
const AGGRESSIVE_TOMES = [
  "tome_nothing_left","tome_regnant","tome_radiant_armor",
  "tome_omnis_corruption_jet","tome_weeping_dagger","tome_lyt_hearted",
  "tome_all_or_nothing","tome_superque",
];
const SSG_TOMES = [
  "tome_temptation_manifested","tome_snake_eyes","tome_nevermore",
  "tome_underdogs_howl","tome_daft_fantasy","tome_goodbye_horses",
  "tome_casino_royale","tome_dragon_deyes","tome_gambits_lucky_cat",
];

// ─────────────────────────────────────────────────────────────
// AWAKENING: SHARED EFFECT UTILITIES (soul dispatcher backbone)
// ─────────────────────────────────────────────────────────────

// Dim the PLAYER's Anima by `amount`, with prophecy-tracking parity
// (buildAnimaLossUpdate — the player side always writes these trackers;
// the NPC's bare writes never did). Returns true if the player LOST
// (anima hit 0) — in which case the match has been ended already.
async function dimPlayerAnima(code, amount) {
  if (amount <= 0) return false;
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const next = Math.max(0, (p.anima ?? 30) - amount);
  await updatePlayer(code, 0, { anima: next, ...buildAnimaLossUpdate(p, amount) });
  if (next <= 0) { await endMatch(code, NPC_IDX); return true; }
  return false;
}

// Dim the NPC's OWN Anima (self-costs like Eternal Nightmare, and the
// Sweet Dreams / Fate Maker parity paths). Returns true if the NPC
// LOST — Fate Maker can genuinely kill the NPC now, same as a human.
async function dimNpcAnima(code, amount) {
  if (amount <= 0) return false;
  const m = await getMatch(code);
  const p = m?.players?.[NPC_IDX] || {};
  const next = Math.max(0, (p.anima ?? 30) - amount);
  await updatePlayer(code, NPC_IDX, { anima: next, ...buildAnimaLossUpdate(p, amount) });
  if (next <= 0) { await endMatch(code, 0); return true; }
  return false;
}

// Mirror of Shatterfield.dimAllOppLyf — dims raw currentLyf on every
// player being (floors at 0). One write, one pause at call site.
async function dimAllPlayerLyf(code, amount) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  let changed = false;
  (f.esclings || []).forEach((s, i) => {
    if (s?.escendant) {
      const card = getCard(s.escendant);
      const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (card?.lyf ?? 0);
      f.esclings[i] = { ...s, currentLyf: Math.max(0, cur - amount) };
      changed = true;
    }
  });
  if (changed) await updatePlayer(code, 0, { field: f });
  return changed;
}

// Ruin one of the PLAYER's face-up tomes by slot index, honoring the
// D'uhmdumn Lord's nullifyFirstRuin soak (the same soak section 10's
// offensive tome destruction respects). MUTATES nothing — reads fresh,
// writes fresh. Returns "ruined" | "soaked" | null.
async function ruinPlayerTomeSlot(code, slotIdx) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  f.tomes = riftArray(f.tomes, 3);
  const t = f.tomes[slotIdx];
  if (!t?.id) return null;
  if (t.nullifyFirstRuin) {
    f.tomes[slotIdx] = { ...t, nullifyFirstRuin: false };
    await updatePlayer(code, 0, { field: f });
    return "soaked";
  }
  const id = t.id;
  f.tomes[slotIdx] = null;
  await updatePlayer(code, 0, { field: f, necropolis: [...(p.necropolis || []), id] });
  return "ruined";
}

// ═════════════════════════════════════════════════════════════
// SEQUENTIAL ACTION HELPERS — each places/activates ONE card,
// writes to Firebase, sleeps, returns true/false (did something).
// Designed to be called in `while(...)` loops by the turn runners.
// ═════════════════════════════════════════════════════════════

// ── Place ONE Lvl 1 escendant into ONE empty slot. ──
// Returns updated { hand, field, placed: true/false }.
//
// AWAKENING LEGALITY FIX (R16): Transcend requires the L1's CLASS to
// match the Escling's class on BOTH paths — Shatterfield.handleTranscend:
// "A pure class mismatch is illegal entirely." Natural additionally
// requires the Ilk to match (free); Deviant Transcend (class match, ilk
// differ) is also free but marks the being permanently Deviant. The old
// code placed ANY L1 into the slot — an illegal move the player could
// never make. Beb now picks randomly among LEGAL options only.
async function placeOneEscendant(code, hand, field, preferNatural) {
  const esclings = field.esclings || [null, null, null];

  // Find first empty slot
  const slotIdx = esclings.findIndex(s => !s?.escendant);
  if (slotIdx === -1) return { hand, field, placed: false };

  const esclingCard = getCard(esclings[slotIdx]?.escling);

  // Find LEGAL Lvl 1s in hand (class must match the Escling — R16)
  const allOpts = hand.map((id, idx) => ({ card: getCard(id), idx }))
    .filter(({ card }) => card?.type === "Escendant" && card?.level === 1)
    .filter(({ card }) => !esclingCard || card.class === esclingCard.class);
  if (allOpts.length === 0) return { hand, field, placed: false };

  const naturalOpts = allOpts.filter(({ card }) =>
    esclingCard && card.class === esclingCard.class && card.ilk === esclingCard.ilk);

  let chosen;
  if (preferNatural) {
    chosen = naturalOpts.length > 0
      ? naturalOpts.sort((a, b) => (b.card.lyt || 0) - (a.card.lyt || 0))[0]
      : allOpts.sort((a, b) => (b.card.lyt || 0) - (a.card.lyt || 0))[0];
  } else {
    chosen = pickRandom(allOpts); // Beb: chaotic, but legal
  }

  const isNatural = esclingCard
    && chosen.card.class === esclingCard.class
    && chosen.card.ilk === esclingCard.ilk;

  const newEsclings = [...esclings];
  newEsclings[slotIdx] = {
    ...newEsclings[slotIdx],
    escendant: hand[chosen.idx],
    currentLyf: chosen.card.lyf ?? null,
    deviant: !isNatural,
    natural: isNatural,
    fallen: false,
    attackedThisTurn: false,
  };
  const newHand = [...hand];
  newHand.splice(chosen.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, placed: true };
}

// ── Ascend ONE being (best target). ──
// Universal rule (Rulebook line 643): only ONE Ascension per turn,
// regardless of Natural or Deviant, regardless of which being. Enforced
// by callers calling this function exactly once per turn — not by a
// per-slot flag. No leaping levels (canAscendNatural enforces L+1 only).
//
// AWAKENING — DEVIANT ASCEND (R21, mirror of Shatterfield.handleAscend):
// When `allowDeviant` is set and NO free natural line exists, the NPC
// considers ANY next-level Escendant in hand. Cost = deviantAscendCost(
// sharedTraits(current, target), target.level), paid in Chao via the
// canonical gate (the player-side handleAscend pays spendChao(cost) —
// mirrored exactly). Deviant status is permanent and sticky:
//   deviant: slot.deviant || !isNatural, natural: isNatural && !slot.deviant
// A Deviant being may still Ascend naturally for free later — but it
// never sheds the mark (Rulebook ch.15: "Once Deviant, always Deviant").
async function ascendOne(code, hand, field, prioritizeHighest, allowDeviant = false) {
  const esclings = field.esclings || [null, null, null];

  // ── Natural candidates (free — R19/R20) ──
  const naturals = [];
  for (let i = 0; i < esclings.length; i++) {
    const slot = esclings[i];
    if (!slot?.escendant || slot.fallen) continue;
    const cur = getCard(slot.escendant);
    if (!cur || cur.level >= 3) continue;
    const opts = hand.map((id, idx) => ({ card: getCard(id), idx }))
      .filter(({ card }) => canAscendNatural(cur, card));
    if (opts.length > 0) naturals.push({ slotIdx: i, opt: opts[0], curLevel: cur.level, cost: 0, natural: true });
  }

  let best = null;
  if (naturals.length > 0) {
    best = prioritizeHighest
      ? naturals.sort((a, b) => b.curLevel - a.curLevel)[0]
      : naturals[0];
  } else if (allowDeviant) {
    // ── Deviant candidates — cheapest fare first, then strongest ──
    const deviants = [];
    for (let i = 0; i < esclings.length; i++) {
      const slot = esclings[i];
      if (!slot?.escendant || slot.fallen) continue;
      const cur = getCard(slot.escendant);
      if (!cur || cur.level >= 3) continue;
      hand.forEach((id, idx) => {
        const card = getCard(id);
        if (card?.type !== "Escendant" || card?.level !== cur.level + 1) return;
        const cost = deviantAscendCost(sharedTraits(cur, card), card.level);
        if (cost <= 0) return; // natural would have caught it
        deviants.push({ slotIdx: i, opt: { card, idx }, curLevel: cur.level, cost, natural: false });
      });
    }
    deviants.sort((a, b) => a.cost - b.cost || (b.opt.card.lyt || 0) - (a.opt.card.lyt || 0));
    best = deviants[0] || null;
  }
  if (!best) return { hand, field, ascended: false };

  // Deviant fare — fresh Firebase gate (Bug F discipline). If the NPC
  // can't afford it right now, the ascension simply doesn't happen.
  // CANON (caP'n ruling, July 2026): Deviant fares are payable in Chao
  // OR Anima — Chao first, Anima fallback (same order as Resurrection).
  // The player-side handleAscend gets the identical fix this deploy.
  if (!best.natural) {
    const paid = await npcPayChaoElseAnima(code, best.cost);
    if (!paid) return { hand, field, ascended: false };
  }

  const card = getCard(hand[best.opt.idx]);

  const newEsclings = [...esclings];
  // ── DESCENT FIX (July 2026, mirrors Shatterfield.handleAscend) ──
  // Push the outgoing lower-level Escendant into the soul so that when
  // this being is later ruined, descendBeing/descendPlayerSlot can find
  // it and the being DESCENDS instead of fully ruining back to Escling.
  // The NPC never did this — which is why NPC beings could never descend.
  const ascSlot = newEsclings[best.slotIdx];
  const prevSoul = ascSlot.soul || [];
  const newSoul = prevSoul.includes(ascSlot.escendant) ? prevSoul : [...prevSoul, ascSlot.escendant];
  newEsclings[best.slotIdx] = {
    ...ascSlot,
    escendant: hand[best.opt.idx],
    currentLyf: card?.lyf ?? null,
    soul: newSoul,
    deviant: ascSlot.deviant || !best.natural,
    natural: best.natural && !ascSlot.deviant,
  };
  const newHand = [...hand];
  newHand.splice(best.opt.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, ascended: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: TRAINING — trainOnePrimera
// Mirror of Shatterfield.handleAttachPrimera: class+ilk must both
// match the being, the Primera's level must be <= the being's level,
// and only one Primera per level may sit in a soul (R71).
// CANON (caP'n ruling, July 2026): this is NATURAL Training — free
// (Rulebook ch.8: "Natural Training — Free. Unlimited per turn").
// A Primera's printed chaoCost is its ACTIVATION fare, paid in
// activateOneSoulCard. DEVIANT Training (attaching a mismatched
// Primera at the shared-traits cost table) is a separate, expensive
// mechanic not yet implemented on EITHER side — backlog, both sides
// together, so the NPC never gains a move the player lacks.
// Attaches ONE per call (one action = one write = one pause); loop it.
// Priority: build the strongest being first (highest being level),
// then the highest Primera level — the road to a Supreme Being (R69)
// runs through the full L1/L2/L3 set.
// ─────────────────────────────────────────────────────────────
async function trainOnePrimera(code, hand, field) {
  const esclings = field.esclings || [];
  const pairs = [];
  hand.forEach((id, idx) => {
    const card = getCard(id);
    if (card?.type !== "Primera") return;
    esclings.forEach((slot, slotIdx) => {
      if (!slot?.escendant) return;
      const being = getCard(slot.escendant);
      if (!being) return;
      if (card.class !== being.class || card.ilk !== being.ilk) return;
      if ((card.level || 1) > (being.level || 1)) return;
      const soul = slot.soul || [];
      const alreadyHasLevel = soul.some(sid => {
        const sc = getCard(sid);
        return sc?.type === "Primera" && sc?.level === card.level;
      });
      if (alreadyHasLevel) return;
      pairs.push({ idx, slotIdx, card, beingLevel: being.level || 1 });
    });
  });
  if (pairs.length === 0) return { hand, field, trained: false };

  pairs.sort((a, b) => b.beingLevel - a.beingLevel || (b.card.level || 0) - (a.card.level || 0));
  const pick = pairs[0];

  const newEsclings = [...esclings];
  const slot = newEsclings[pick.slotIdx];
  newEsclings[pick.slotIdx] = { ...slot, soul: [...(slot.soul || []), hand[pick.idx]] };
  const newHand = [...hand];
  newHand.splice(pick.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, trained: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: U'HLTI + AFFINITY — attachOneSoulCard
// Mirror of Shatterfield.handleAttachSoulCard:
//   U'hlti  — owner-bound (card.owner === the exact escendant id),
//             Natural beings only (R64), Level 3 only, one per being
//             (R72). Free to attach.
//   Affinity — Natural L3 only, REQUIRES the U'hlti already attached
//             (R70), one per being (R73). Free to attach.
// This is the final third of the Supreme Being checklist (R69):
// Natural L3 + full Primera set + U'hlti + Affinity.
// ─────────────────────────────────────────────────────────────
async function attachOneSoulCard(code, hand, field) {
  const esclings = field.esclings || [];
  for (let idx = 0; idx < hand.length; idx++) {
    const card = getCard(hand[idx]);
    if (!card || (card.type !== "U'hlti" && card.type !== "Affinity")) continue;
    for (let slotIdx = 0; slotIdx < esclings.length; slotIdx++) {
      const slot = esclings[slotIdx];
      if (!slot?.escendant) continue;
      const being = getCard(slot.escendant);
      if (!being || (being.level || 1) < 3) continue;
      if (slot.deviant || slot.natural === false) continue; // R64 — Deviants never wear these
      const soul = slot.soul || [];
      const hasUhlti = soul.some(sid => getCard(sid)?.type === "U'hlti");
      const hasAffinity = soul.some(sid => getCard(sid)?.type === "Affinity");
      if (card.type === "U'hlti") {
        if (card.owner !== slot.escendant) continue; // belongs to a different being
        if (hasUhlti) continue;                       // R72
      } else {
        if (!hasUhlti) continue;    // R70 — U'hlti first
        if (hasAffinity) continue;  // R73
      }
      const newEsclings = [...esclings];
      newEsclings[slotIdx] = { ...slot, soul: [...soul, hand[idx]] };
      const newHand = [...hand];
      newHand.splice(idx, 1);
      const newField = { ...field, esclings: newEsclings };
      await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
      await sleep(ACTION_DELAY);
      return { hand: newHand, field: newField, attached: true };
    }
  }
  return { hand, field, attached: false };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: SOUL CARD ACTIVATION — compact mirror of
// Shatterfield.handleActivateSoulCard for every Primera / U'hlti /
// Affinity shipped in the 12 starter decks.
//
// Contract, per activation:
//   1. Skip passives and anything not implemented (never pay for
//      nothing — that's the exact bug class the tome ledger fought).
//   2. "Useful right now" gate — soulEffectUseful. No redundant flags,
//      no suicidal self-dims, no dead tutors.
//   3. Pay fares fresh (mirror player order: U'hlti animaCost first,
//      then chaoCost — Shatterfield 4366-4374).
//   4. One activation per soul card per turn (the `used` Set). The
//      player side currently has NO cooldown gate on soul cards; the
//      NPC deliberately stays modest — one firing each per turn.
//   5. Effects that mutate the NPC's OWN field operate on the CALLER's
//      field draft (never a fresh read) so the runner's end-of-turn
//      full-field write cannot clobber them.
//
// Returns { hand, field, grimoire, activated, matchOver }. matchOver
// means the match ENDED inside the effect (either side hit 0 Anima) —
// the runner must stop immediately.
// ─────────────────────────────────────────────────────────────
const SOUL_PASSIVE_IDS = new Set([
  // True passives — their effect lives in effLytPure/effLyfPure or in
  // attack riders; "activating" them player-side just shows an info toast.
  "p_h09_1", "p_xulan_1", "p_dg0_1", "p_antdroid_3",
  "p_premier_dragon_1", "p_premier_dragon_2", "p_chaos_dragon_3",
  "u_xulan_lord", "u_z4z_lord",
  // Deliberately skipped (needs state the NPC doesn't track, or
  // canon-sensitive interactions) — never paid for, never fired:
  "p_dg0_3", "p_premier_dragon_3", "p_chaos_dragon_2",
]);

const CHAMPION_SOUL_IDS = new Set([
  "p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3",   // Intangible
  "p_h09_3", "aff_holy",                            // Nullify
  "p_xulan_2", "p_inverted_serpent_2",              // Dim-on-attack
  "p_spectral_tiger_3",                             // Dim all Lyf
  "u_h01_lord", "u_shadow_fox_lord", "u_spectral_tiger_lord", "u_h09_lord",
  "p_dg0_2",                                        // Cheap draw
]);

function soulEffectUseful(id, slot, slotIdx, ctx) {
  const { field, playerField, playerLive, npcAnima, turn, hand, grimoire,
          playerStanding, npcStanding, myHighLevel, difficulty } = ctx;
  const beingLevel = getCard(slot.escendant)?.level || 1;

  if (["p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3"].includes(id)) return !slot.intangible;
  if (id === "p_h01_2") return !slot.mustBeRuinedFirst && npcStanding >= 2 && beingLevel < myHighLevel;
  if (id === "p_h09_3" || id === "aff_holy") return !slot.nullifyNextAttack;
  if (id === "p_lucky_dragon_3") return !slot.reflectNextAttack;
  if (["p_xulan_2", "p_inverted_serpent_2"].includes(id)) return !slot.dimAnimaOnAttack;
  if (["p_shadow_fox_1", "p_spectral_tiger_1", "p_inverted_serpent_1"].includes(id)) return !slot.canDirectAnima;
  if (id === "p_spectral_tiger_2") return !slot.spectralClaws;
  if (id === "u_inverted_serpent_lord") return !slot.invFangsBuff;
  if (id === "p_xulan_3") return !slot.nullifyNextAttack && npcAnima > 12;
  if (id === "p_z4z_1") return turn >= 2 && npcAnima > 15 && !slot.extraAttack;
  if (id === "p_z4z_2") {
    if (slot.extraAttack || turn < 2) return false;
    return (field.esclings || []).some((s, i) => {
      if (i === slotIdx) return false;
      const c = getCard(s?.escendant);
      return c && c.ilk === "Time" && c.level === 2;
    });
  }
  if (id === "p_z4z_3") return turn >= 2 && npcAnima > 20 && playerAnimaClear(playerField);
  if (id === "p_antdroid_1") return turn >= 2 && npcAnima > 15 && playerAnimaClear(playerField) && !slot.extraAttackAnimaOnly;
  if (id === "p_antdroid_2") return npcAnima > 12 && hand.length < 6 && Object.keys(grimoire || {}).length > 0;
  if (id === "p_dg0_2") return npcAnima > 6 && hand.length < 7 && Object.keys(grimoire || {}).length > 0;
  if (["p_lucky_dragon_1", "p_lucky_dragon_2"].includes(id)) return hand.length < 7;
  if (id === "p_h01_3") {
    return hand.length < 7 && Object.keys(grimoire || {}).some(gid => {
      const c = getCard(gid); return c?.ilk === "Space" && c?.type === "Escendant";
    });
  }
  if (id === "p_h09_2") {
    return hand.length < 6 && Object.keys(grimoire || {}).some(gid => {
      const c = getCard(gid); return c?.ilk === "Space" && c?.type === "Escendant" && (c.level || 1) <= 2;
    });
  }
  if (id === "aff_jynxi") return hand.length < 7;
  if (["u_h01_lord", "u_premier_dragon_lord", "u_shadow_fox_lord",
       "u_spectral_tiger_lord", "u_h09_lord"].includes(id)) return turn >= 2;
  if (id === "p_spectral_tiger_3") return playerStanding >= 1;
  if (id === "p_inverted_serpent_3") return ((playerField?.esclings) || []).some(s => s?.intangible);
  if (id === "u_antdroid_lord") {
    if (getRequiredTargetNpc(playerField)) return false;
    const lethal = totalAvailableLyt(field, playerField) >= (playerLive.anima ?? 30);
    return lethal || (playerLive.anima ?? 30) < 10;
  }
  if (id === "u_chaos_dragon_lord") return playerStanding > npcStanding && npcStanding >= 1;
  if (id === "u_dg0_lord") return true; // always has the draw fallback
  if (id === "p_chaos_dragon_1") {
    const tomes = riftArray(playerField?.tomes, 3);
    return tomes.some(t => t?.id && !t.faceDown) || (playerField?.curios || []).some(c => c);
  }
  if (id === "aff_evil") {
    if (getRequiredTargetNpc(playerField)) return false; // mirror: X'aol refuses under MRF
    return playerStanding > 0
      || riftArray(playerField?.tomes, 3).some(t => t?.id && !t.faceDown)
      || (playerField?.curios || []).some(c => c);
  }
  if (id === "aff_awoken") {
    return ((playerField?.esclings) || []).some(s => (s?.soul || []).some(sid => getCard(sid)?.type === "Affinity"));
  }
  if (id === "u_lucky_dragon_lord") return difficulty === "Beb"; // only the chaotic one gambles
  return false; // unknown / unimplemented — never pay for nothing
}

async function applyNpcSoulEffect(code, id, slotIdx, ctx) {
  let { hand, field, grimoire } = ctx;
  const playerLive = ctx.playerLive || {};
  const turn = ctx.turn || 0;
  let matchOver = false;

  // Set flags on the CALLER's field draft + write (never a fresh read —
  // the runner's end-of-turn full-field write must not clobber these).
  const setSlotFlags = async (flags) => {
    const newEsclings = [...(field.esclings || [])];
    newEsclings[slotIdx] = { ...newEsclings[slotIdx], ...flags };
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
  };

  const drawFromGrim = async (count) => {
    const newHand = [...hand];
    const newGrim = { ...grimoire };
    for (let d = 0; d < count; d++) {
      const pool = Object.keys(newGrim).filter(gid => newGrim[gid] > 0);
      if (pool.length === 0) break;
      const drawn = pickRandom(pool);
      newHand.push(drawn);
      if (newGrim[drawn] > 1) newGrim[drawn]--; else delete newGrim[drawn];
    }
    hand = newHand; grimoire = newGrim;
    await updatePlayer(code, NPC_IDX, { hand, grimoire });
  };

  // Tutor from the Grimoire — prefers cards that complete an ascension
  // line for a standing being, else takes the highest level available.
  // NOTE: the NPC DEPLETES its Grimoire on a tutor (rules-true). The
  // player-side searchAndAdd currently does NOT — flagged as a
  // player-side bug in the Awakening tally, not mirrored here.
  const tutorFromGrim = async (filter, count) => {
    const wantIds = [];
    (field.esclings || []).forEach(s => {
      const cur = getCard(s?.escendant);
      if (!cur || cur.level >= 3 || s?.fallen) return;
      Object.keys(grimoire || {}).forEach(gid => {
        const c = getCard(gid);
        if (c && canAscendNatural(cur, c) && filter(c)) wantIds.push(gid);
      });
    });
    const newHand = [...hand];
    const newGrim = { ...grimoire };
    for (let d = 0; d < count; d++) {
      const pool = Object.keys(newGrim).filter(gid => newGrim[gid] > 0 && filter(getCard(gid)));
      if (pool.length === 0) break;
      const chosen = pool.find(gid => wantIds.includes(gid))
        || pool.sort((a, b) => (getCard(b)?.level || 0) - (getCard(a)?.level || 0))[0];
      newHand.push(chosen);
      if (newGrim[chosen] > 1) newGrim[chosen]--; else delete newGrim[chosen];
    }
    hand = newHand; grimoire = newGrim;
    await updatePlayer(code, NPC_IDX, { hand, grimoire });
  };

  const done = () => ({ hand, field, grimoire, matchOver });

  // ── Self-flag setters ──
  if (["p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3"].includes(id)) {
    await setSlotFlags({ intangible: true }); return done();
  }
  if (id === "p_h01_2") { await setSlotFlags({ mustBeRuinedFirst: true }); return done(); }
  if (id === "p_h09_3" || id === "aff_holy") { await setSlotFlags({ nullifyNextAttack: true }); return done(); }
  if (id === "p_lucky_dragon_3") { await setSlotFlags({ reflectNextAttack: true }); return done(); }
  if (["p_xulan_2", "p_inverted_serpent_2"].includes(id)) { await setSlotFlags({ dimAnimaOnAttack: 1 }); return done(); }
  if (["p_shadow_fox_1", "p_spectral_tiger_1", "p_inverted_serpent_1"].includes(id)) {
    await setSlotFlags({ canDirectAnima: true }); return done();
  }
  if (id === "p_spectral_tiger_2") { await setSlotFlags({ spectralClaws: true }); return done(); }
  if (id === "u_inverted_serpent_lord") { await setSlotFlags({ invFangsBuff: true }); return done(); }

  // ── Self-Anima costs + flags ──
  if (id === "p_xulan_3") {
    if (await dimNpcAnima(code, 4)) { matchOver = true; return done(); }
    await setSlotFlags({ nullifyNextAttack: true }); return done();
  }
  if (id === "p_z4z_1") {
    if (await dimNpcAnima(code, 5)) { matchOver = true; return done(); }
    await setSlotFlags({ extraAttack: true }); return done();
  }
  if (id === "p_z4z_2") { await setSlotFlags({ extraAttack: true }); return done(); }
  if (id === "p_z4z_3") {
    if (await dimNpcAnima(code, 8)) { matchOver = true; return done(); }
    const newEsclings = (field.esclings || []).map(s => s?.escendant ? { ...s, extraAttack: true } : s);
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
    return done();
  }
  if (id === "p_antdroid_1") {
    if (await dimNpcAnima(code, 5)) { matchOver = true; return done(); }
    await setSlotFlags({ extraAttackAnimaOnly: true }); return done();
  }

  // ── Draws / tutors ──
  if (id === "p_dg0_2") {
    if (await dimNpcAnima(code, 1)) { matchOver = true; return done(); }
    await drawFromGrim(1); return done();
  }
  if (id === "p_antdroid_2") {
    if (await dimNpcAnima(code, 4)) { matchOver = true; return done(); }
    await drawFromGrim(2); return done();
  }
  if (id === "p_h01_3") {
    await tutorFromGrim(c => c?.ilk === "Space" && c?.type === "Escendant", 1); return done();
  }
  if (id === "p_h09_2") {
    await tutorFromGrim(c => c?.ilk === "Space" && c?.type === "Escendant" && (c?.level || 1) <= 2, 2); return done();
  }
  if (id === "aff_jynxi") {
    // Friend or Faux — add 1 Shadow Gambling from Grimoire or Necropolis.
    const isSSGCard = (gid) => SSG_TOMES.includes(gid) || getCard(gid)?.ssg;
    const inGrim = Object.keys(grimoire || {}).find(gid => isSSGCard(gid) && (grimoire[gid] || 0) > 0);
    if (inGrim) {
      const newGrim = { ...grimoire };
      if (newGrim[inGrim] > 1) newGrim[inGrim]--; else delete newGrim[inGrim];
      hand = [...hand, inGrim]; grimoire = newGrim;
      await updatePlayer(code, NPC_IDX, { hand, grimoire });
      return done();
    }
    const mFresh = await getMatch(code);
    const nec = [...((mFresh?.players?.[NPC_IDX]?.necropolis) || [])];
    const necIdx = nec.findIndex(nid => isSSGCard(nid));
    if (necIdx >= 0) {
      const picked = nec.splice(necIdx, 1)[0];
      hand = [...hand, picked];
      await updatePlayer(code, NPC_IDX, { hand, necropolis: nec });
    }
    return done();
  }
  if (id === "p_lucky_dragon_1" || id === "p_lucky_dragon_2") {
    const count = id === "p_lucky_dragon_1" ? 1 : 2;
    if (Math.random() < 0.5) {
      await drawFromGrim(count); // heads — the NPC draws
    } else {
      // tails — the PLAYER draws (mirror: from their own Grimoire pool;
      // the player-side implementation does not deplete on the coin).
      const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
      const pool = Object.entries(pFresh.grimoire || {}).flatMap(([gid, qty]) => Array(qty).fill(gid));
      const drawn = pool.sort(() => Math.random() - 0.5).slice(0, count);
      if (drawn.length > 0) await updatePlayer(code, 0, { hand: [...(pFresh.hand || []), ...drawn] });
    }
    return done();
  }

  // ── Player-facing dims (win checks live inside dimPlayerAnima) ──
  if (id === "u_h01_lord") { matchOver = await dimPlayerAnima(code, 4); return done(); }
  if (id === "u_premier_dragon_lord") { matchOver = await dimPlayerAnima(code, 10); return done(); }
  if (id === "u_shadow_fox_lord") {
    await dimAllPlayerLyf(code, 2);
    matchOver = await dimPlayerAnima(code, 1); return done();
  }
  if (id === "u_spectral_tiger_lord") {
    await dimAllPlayerLyf(code, 1);
    matchOver = await dimPlayerAnima(code, 2); return done();
  }
  if (id === "u_h09_lord") {
    // Radiant Shower — the choice: wide Lyf pressure vs direct damage.
    if (standingBeings(playerLive.field || {}).length >= 2) await dimAllPlayerLyf(code, 1);
    else matchOver = await dimPlayerAnima(code, 2);
    return done();
  }
  if (id === "p_spectral_tiger_3") { await dimAllPlayerLyf(code, 2); return done(); }
  if (id === "p_inverted_serpent_3") {
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const cnt = ((pFresh.field?.esclings) || []).filter(s => s?.intangible).length;
    if (cnt > 0) matchOver = await dimPlayerAnima(code, cnt * 3);
    return done();
  }

  // ── Board manipulation ──
  if (id === "u_antdroid_lord") {
    const newEsclings = (field.esclings || []).map(s =>
      s?.escendant && !s.fallen ? { ...s, animaRedirectAll: true } : s);
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
    return done();
  }
  if (id === "u_chaos_dragon_lord") {
    // I or None — both Warlocks ruin beings until 1 remains. Universal
    // ruin semantics on BOTH sides via descendPlayerSlot. The NPC's own
    // descents mutate the CALLER's field draft (sync discipline).
    const mFresh = await getMatch(code);
    const npcNec = [...((mFresh?.players?.[NPC_IDX]?.necropolis) || [])];
    const myAlive = (field.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
    for (const { i } of myAlive.slice(0, -1)) {
      descendPlayerSlot(field, npcNec, i, turn);
      await updatePlayer(code, NPC_IDX, { field, necropolis: npcNec });
      await sleep(POST_RUIN_DELAY);
    }
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pField = JSON.parse(JSON.stringify(pFresh.field || {}));
    pField.tomes = riftArray(pField.tomes, 3);
    const pNec = [...(pFresh.necropolis || [])];
    const pAlive = (pField.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
    for (const { i } of pAlive.slice(0, -1)) {
      descendPlayerSlot(pField, pNec, i, turn);
      await updatePlayer(code, 0, { field: pField, necropolis: pNec });
      await sleep(POST_RUIN_DELAY);
    }
    return done();
  }
  if (id === "u_dg0_lord") {
    // Justice Reigns — ruin 1 opp Non-Being, else draw 1. The NPC never
    // targets a Prophecy (Ruling 5: prophecies are structural).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pTomes = riftArray(pFresh.field?.tomes, 3);
    const mrf = getRuinFirstTome(pFresh.field);
    const tomeIdx = mrf ? mrf.slotIdx : pTomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curios = pFresh.field?.curios || [];
    const curioIdx = curios.findIndex(c => c);
    if (curioIdx >= 0) {
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      const cid = pf.curios[curioIdx];
      pf.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), cid] });
      await sleep(ACTION_DELAY);
      return done();
    }
    const soulTargets = (pFresh.field?.esclings || []).flatMap((s, si) =>
      (s?.soul || []).filter(sid => {
        const c = getCard(sid);
        return c?.type === "Primera" || c?.type === "U'hlti";
      }).map(sid => ({ sid, si })));
    if (soulTargets.length > 0) {
      const pick = soulTargets.sort((a, b) => (getCard(b.sid)?.level || 0) - (getCard(a.sid)?.level || 0))[0];
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      pf.esclings[pick.si] = {
        ...pf.esclings[pick.si],
        soul: (pf.esclings[pick.si].soul || []).filter(sid => sid !== pick.sid),
      };
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), pick.sid] });
      await sleep(ACTION_DELAY);
      return done();
    }
    await drawFromGrim(1);
    return done();
  }
  if (id === "p_chaos_dragon_1") {
    // Natural Disaster — ruin 1 opp Non-Being (Tome or Curio). MRF tome
    // eats the effect first (mirrors section 10's priority).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pTomes = riftArray(pFresh.field?.tomes, 3);
    const mrf = getRuinFirstTome(pFresh.field);
    const tomeIdx = mrf ? mrf.slotIdx : pTomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curios = pFresh.field?.curios || [];
    const curioIdx = curios.findIndex(c => c);
    if (curioIdx >= 0) {
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      const cid = pf.curios[curioIdx];
      pf.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), cid] });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "aff_evil") {
    // X'aol — send 1 player card to Necropolis (never an Affinity).
    // Priority: strongest standing being (universal ruin semantics) →
    // face-up tome (soak-aware) → curio. Refused under any MRF (mirror).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pField = JSON.parse(JSON.stringify(pFresh.field || {}));
    pField.tomes = riftArray(pField.tomes, 3);
    if (getRequiredTargetNpc(pField)) return done();
    const pNec = [...(pFresh.necropolis || [])];
    const beings = (pField.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant && !s.fallen);
    if (beings.length > 0) {
      const target = beings.sort((a, b) =>
        (getCard(b.s.escendant)?.level || 0) - (getCard(a.s.escendant)?.level || 0))[0];
      descendPlayerSlot(pField, pNec, target.i, turn);
      await updatePlayer(code, 0, { field: pField, necropolis: pNec });
      await sleep(POST_RUIN_DELAY);
      return done();
    }
    const tomeIdx = pField.tomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curioIdx = (pField.curios || []).findIndex(c => c);
    if (curioIdx >= 0) {
      const cid = pField.curios[curioIdx];
      pField.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pField, necropolis: [...pNec, cid] });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "aff_awoken") {
    // Enlightenment — nullify ONE player Affinity. Mirror exactly:
    // removed from the soul, NOT sent to Necropolis (nullified).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const affTargets = (pFresh.field?.esclings || []).flatMap((s, si) =>
      (s?.soul || []).filter(sid => getCard(sid)?.type === "Affinity").map(sid => ({ sid, si })));
    if (affTargets.length > 0) {
      const pick = affTargets[0];
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      pf.esclings[pick.si] = {
        ...pf.esclings[pick.si],
        soul: (pf.esclings[pick.si].soul || []).filter(sid => sid !== pick.sid),
      };
      await updatePlayer(code, 0, { field: pf });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "u_lucky_dragon_lord") {
    // Dragon Eyes — roll 2 dice; sum 2 = +2 Shadow Gambling streak +
    // dragonEyesBonus; anything else breaks the streak. Beb-only gamble.
    const mFresh = await getMatch(code);
    const npcLive = mFresh?.players?.[NPC_IDX] || {};
    const d1 = Math.floor(Math.random() * 6) + 1;
    const d2 = Math.floor(Math.random() * 6) + 1;
    if (d1 + d2 === 2) {
      field = { ...field, dragonEyesBonus: true };
      await updatePlayer(code, NPC_IDX, {
        shadowGamblingStreak: (npcLive.shadowGamblingStreak || 0) + 2,
        field,
      });
    } else {
      await updatePlayer(code, NPC_IDX, { shadowGamblingStreak: 0 });
    }
    return done();
  }

  return done(); // unknown id — nothing applied (gate should prevent this)
}

async function activateOneSoulCard(code, hand, field, grimoire, turn, used, difficulty) {
  const m = await getMatch(code);
  const npcLive = m?.players?.[NPC_IDX] || {};
  const playerLive = m?.players?.[0] || {};
  const playerField = playerLive.field || {};
  const npcAnima = npcLive.anima ?? 30;
  const esclings = field.esclings || [];
  const ctxGate = {
    field, playerField, playerLive, npcAnima, turn, hand, grimoire,
    playerStanding: standingBeings(playerField).length,
    npcStanding: standingBeings(field).length,
    myHighLevel: highestLevel(esclings),
    difficulty,
  };

  for (let slotIdx = 0; slotIdx < esclings.length; slotIdx++) {
    const slot = esclings[slotIdx];
    if (!slot?.escendant) continue;
    const soul = slot.soul || [];
    for (const soulId of soul) {
      const key = slotIdx + ":" + soulId;
      if (used.has(key)) continue;
      if (SOUL_PASSIVE_IDS.has(soulId)) continue;
      const card = getCard(soulId);
      if (!card) continue;
      if (difficulty === "Champion" && !CHAMPION_SOUL_IDS.has(soulId)) continue;

      if (!soulEffectUseful(soulId, slot, slotIdx, ctxGate)) continue;

      // ── Pay fares fresh (mirror player order: Anima, then Chao) ──
      const cost = card.chaoCost || 0;
      const animaCost = card.type === "U'hlti" ? (card.animaCost || 0) : 0;
      if ((npcLive.chao ?? 0) < cost) continue;
      if (animaCost > 0 && npcAnima <= animaCost + 4) continue; // never suicide on a fare
      if (animaCost > 0 && !(await npcSpendAnima(code, animaCost))) continue;
      if (cost > 0 && !(await npcSpendChao(code, cost))) continue;

      used.add(key);
      // ── RESPONSE WINDOW — a soul-card effect is a card activation
      // (canon: "any card effect in use" — Sword & Shield semantics).
      // Nihil voids this firing, fares stay spent. Omnia ruins the
      // soul card OFF the being: spliced from the Soul, sent to the
      // NPC's Necropolis, effect never resolves.
      // COUNTER HIERARCHY (caP'n ruling): a LATE counter (L1/L2 vs a
      // L3+ soul card) cannot pre-empt — the effect resolves first,
      // then the soul card must leave the field all the same.
      let lateCounter = false;
      {
        const rw = await requestWindow({ kind: "ACTIVATION", cardId: soulId });
        if (rw?.counter && rw?.late) {
          lateCounter = true; // effect proceeds below; splice after
        } else if (rw?.counter) {
          if (rw.counter === "omnia") {
            const slotNow = field.esclings?.[slotIdx];
            if (slotNow) {
              const newEsc = [...(field.esclings || [])];
              newEsc[slotIdx] = { ...slotNow, soul: (slotNow.soul || []).filter(id => id !== soulId) };
              field = { ...field, esclings: newEsc };
              const mNec = await getMatch(code);
              const npcNec = [...(mNec?.players?.[NPC_IDX]?.necropolis || []), soulId];
              await updatePlayer(code, NPC_IDX, { field, necropolis: npcNec });
            }
          }
          await sleep(TOME_INTERRUPT);
          return { hand, field, grimoire, activated: true, matchOver: false };
        }
      }
      const r = await applyNpcSoulEffect(code, soulId, slotIdx, { hand, field, grimoire, playerLive, turn });
      if (lateCounter && !r.matchOver) {
        // Effect has resolved — now the countered soul card leaves.
        const fld = r.field || field;
        const slotNow = fld.esclings?.[slotIdx];
        if (slotNow && (slotNow.soul || []).includes(soulId)) {
          const newEsc = [...(fld.esclings || [])];
          newEsc[slotIdx] = { ...slotNow, soul: (slotNow.soul || []).filter(id => id !== soulId) };
          r.field = { ...fld, esclings: newEsc };
          const mNec = await getMatch(code);
          const npcNec = [...(mNec?.players?.[NPC_IDX]?.necropolis || []), soulId];
          await updatePlayer(code, NPC_IDX, { field: r.field, necropolis: npcNec });
        }
      }
      await sleep(TOME_INTERRUPT);
      return { hand: r.hand, field: r.field, grimoire: r.grimoire, activated: true, matchOver: r.matchOver || false };
    }
  }
  return { hand, field, grimoire, activated: false, matchOver: false };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: GRIMOIRE SEARCH — mirror of handleGrimoireSearch.
// Pay 2 (L1) / 4 (L2+) Chao-else-Anima to tutor a Being of the chosen
// level from the Grimoire into hand. CONSUMES the turn's draw — the
// caller must treat a successful search as its draw for the turn.
// The player-side pay-then-refund-on-empty dance is avoided by
// pre-checking the pool BEFORE paying (same net rules, no dead writes).
// `wantIds` lets the caller steer the pick toward a specific ascension
// line; otherwise the highest-Lyt candidate is taken.
// ─────────────────────────────────────────────────────────────
async function grimoireSearchOne(code, hand, grimoire, level, wantIds = null) {
  const pool = Object.keys(grimoire || {}).filter(id => {
    const c = getCard(id);
    return c?.type === "Escendant" && c?.level === level && (grimoire[id] || 0) > 0;
  });
  if (pool.length === 0) return { hand, grimoire, searched: false };
  const cost = level === 1 ? 2 : 4;
  if (!(await npcPayChaoElseAnima(code, cost))) return { hand, grimoire, searched: false };

  let chosen = null;
  if (wantIds && wantIds.length) chosen = pool.find(id => wantIds.includes(id)) || null;
  if (!chosen) chosen = pool.sort((a, b) => (getCard(b)?.lyt || 0) - (getCard(a)?.lyt || 0))[0];

  const newGrimoire = { ...grimoire };
  if (newGrimoire[chosen] > 1) newGrimoire[chosen]--; else delete newGrimoire[chosen];
  const newHand = [...hand, chosen];
  await updatePlayer(code, NPC_IDX, { hand: newHand, grimoire: newGrimoire });
  await sleep(SEARCH_DELAY);
  return { hand: newHand, grimoire: newGrimoire, searched: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: LUXMOIRE SEARCH — mirror of handleLuxmoireSearch.
// 5 Chao to tutor ANY Primera from the Luxmoire into hand.
// CANON (caP'n ruling, July 4 2026): the search CONSUMES the turn's
// draw, exactly like Grimoire Search — the live player modal sets
// drawnThisTurn and that is the rule. (Rulebook ch.8's "does not
// consume your draw" line is the stale text; caP'n is amending it.)
// Callers must treat a successful search as the turn's draw.
// The NPC only ever searches for a card it can USE right now — a
// Primera that is trainable this instant (legal target standing, no
// duplicate-level conflict), or (DEPTHS BRAIN, prophecy push) a missing
// Supreme piece passed in via extraWantIds. No dead tutors.
// LEGAL PARITY NOTE: the player's Luxmoire Search modal offers EVERY
// Luxmoire card (Primeras, U'hltis, Affinities) — the old Primera-only
// restriction here was NPC conservatism, not a rule. The fare is also
// Chao-ELSE-Anima on the player side (need 5 Chao or 5 Anima) — now
// mirrored via npcPayChaoElseAnima instead of Chao-only.
// ─────────────────────────────────────────────────────────────
async function luxmoireSearchOne(code, hand, field, luxmoire, extraWantIds = null) {
  const esclings = field.esclings || [];
  const trainable = Object.keys(luxmoire || {}).filter(id => {
    if ((luxmoire[id] || 0) <= 0) return false;
    const card = getCard(id);
    if (card?.type !== "Primera") return false;
    return esclings.some(slot => {
      if (!slot?.escendant) return false;
      const being = getCard(slot.escendant);
      if (!being) return false;
      if (card.class !== being.class || card.ilk !== being.ilk) return false;
      if ((card.level || 1) > (being.level || 1)) return false;
      const soul = slot.soul || [];
      return !soul.some(sid => {
        const sc = getCard(sid);
        return sc?.type === "Primera" && sc?.level === card.level;
      });
    });
  });
  const extras = (extraWantIds || []).filter(id => (luxmoire?.[id] || 0) > 0);
  if (trainable.length === 0 && extras.length === 0) return { hand, luxmoire, searched: false };
  if (!(await npcPayChaoElseAnima(code, 5))) return { hand, luxmoire, searched: false };

  // Supreme pieces first (they only arrive during a prophecy push),
  // then the highest-level trainable Primera.
  const chosen = extras[0]
    || trainable.sort((a, b) => (getCard(b)?.level || 0) - (getCard(a)?.level || 0))[0];
  const newLuxmoire = { ...luxmoire };
  if (newLuxmoire[chosen] > 1) newLuxmoire[chosen]--; else delete newLuxmoire[chosen];
  const newHand = [...hand, chosen];
  await updatePlayer(code, NPC_IDX, { hand: newHand, luxmoire: newLuxmoire });
  await sleep(SEARCH_DELAY);
  return { hand: newHand, luxmoire: newLuxmoire, searched: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: RULES-TRUE RESURRECTION — mirror of handleResurrect +
// findResurrectSlot (Shatterfield ~7095-7160, ~7401-7420).
//   R44 — once per CARD INSTANCE per Solrend (resurrectedCards,
//         append-only; never overwritten).
//   R45/R46 — resurrect in order: an L1 lands on an empty Escling
//         slot; an L2/L3 lands ONLY on a slot whose current escendant
//         is the (level-1) of the SAME natural chain (sharedTraits
//         === 2) — never straight onto a bare Escling.
//   R47 — cost 2 (L1) / 4 (L2/L3), paid Chao-else-Anima.
//   R48 — souls preserved; CHAIN FIX — the outgoing lower level is
//         souled so later descents can find it (July 2026 parity).
// The OLD NPC resurrect could drop the highest-level Necropolis being
// straight onto a bare Escling (illegal) and could revive the same
// card every turn forever (no R44 record). Both closed here.
// Reads necropolis/resurrectedCards FRESH — never a turn-start snapshot.
// ─────────────────────────────────────────────────────────────
async function resurrectOne(code, field, matchTurn) {
  const m = await getMatch(code);
  const npcLive = m?.players?.[NPC_IDX] || {};
  const necropolis = [...(npcLive.necropolis || [])];
  const revived = npcLive.resurrectedCards || [];
  const esclings = field.esclings || [];

  const findResurrectSlot = (card) => {
    if (card.level === 1) {
      return esclings.findIndex(s => !s?.escendant);
    }
    return esclings.findIndex(s => {
      if (!s?.escendant) return false;
      const current = getCard(s.escendant);
      if (!current) return false;
      if (current.level !== card.level - 1) return false;
      return sharedTraits(current, card) === 2;
    });
  };

  const seen = new Set();
  const candidates = [];
  necropolis.forEach(id => {
    if (seen.has(id)) return;
    if (revived.includes(id)) return; // R44
    const c = getCard(id);
    if (c?.type !== "Escendant") return;
    const slotIdx = findResurrectSlot(c);
    if (slotIdx < 0) return;
    seen.add(id);
    candidates.push({ id, card: c, slotIdx });
  });
  if (candidates.length === 0) return { field, resurrected: false };

  // Highest level first — an L3 revived over its standing L2 (full
  // soul intact) is the biggest tempo swing available to the NPC.
  candidates.sort((a, b) => (b.card.level || 0) - (a.card.level || 0));
  const pick = candidates[0];

  const cost = pick.card.level === 1 ? 2 : 4;
  if (!(await npcPayChaoElseAnima(code, cost))) return { field, resurrected: false };

  const newEsclings = [...esclings];
  const resSlot = newEsclings[pick.slotIdx] || {};
  // CHAIN FIX — soul the outgoing lower level so descents can find it
  const resPrevSoul = resSlot.soul || [];
  const resSoul = resSlot.escendant && !resPrevSoul.includes(resSlot.escendant)
    ? [...resPrevSoul, resSlot.escendant]
    : resPrevSoul;
  newEsclings[pick.slotIdx] = {
    ...resSlot,
    escendant: pick.id,
    soul: resSoul,
    fallen: false, fallenTurn: null,
    currentLyf: pick.card.lyf ?? null,
    attackedThisTurn: false,
    intangible: false,
  };
  const necIdx = necropolis.indexOf(pick.id);
  if (necIdx !== -1) necropolis.splice(necIdx, 1);

  const newField = { ...field, esclings: newEsclings };
  await updatePlayer(code, NPC_IDX, {
    field: newField,
    necropolis,
    resurrectedCards: [...revived, pick.id], // R44 record — append-only
  });
  await sleep(RESURRECT_DELAY);
  return { field: newField, resurrected: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: SHARED TOME FIRE-EFFECTS (flip + re-activation)
// Extracted so a tome's simple effect fires the FIRST time it flips
// face-up too — the flip IS the activation per Rulebook line 300. The
// old code paid the cost on flip but applied no effect until the next
// turn's re-activation pass, which non-persistent tomes never even
// reached (they'd already ruined). Covers the resource tomes, the
// Gauntlets bond (Bug #2 — now bonds on first flip, not just
// re-activation), Solomon (Bug G), and the SSG coin-flip draw.
//
// SYNC DISCIPLINE: draws (SSG) mutate the CALLER's hand/grimoire
// locals and return them — the old inline SSG draw wrote fresh-read
// state that the runner's end-of-turn write then CLOBBERED (the drawn
// card silently vanished back into the Grimoire). Same for Solomon's
// self-ruin: it now operates on the caller's field draft.
// The full applyNpcTomeEffect dispatcher remains a future project.
// ─────────────────────────────────────────────────────────────
async function applyNpcTomeFireEffects(code, card, newField, tomes, slotIdx, hand, grimoire) {
  const m = await getMatch(code);
  const npcLive = m?.players?.[NPC_IDX] || {};

  if (card.id === "tome_chao_garden") {
    await updatePlayer(code, NPC_IDX, { maxChao: Math.min(15, (npcLive.maxChao || 3) + 1) });
  }
  if (card.id === "tome_chao_arcadia" || card.id === "tome_chao_paradise") {
    await updatePlayer(code, NPC_IDX, { maxChao: Math.min(15, (npcLive.maxChao || 3) + 2) });
  }
  if (card.id === "tome_bhodi_tree") {
    await updatePlayer(code, NPC_IDX, { anima: Math.min(50, (npcLive.anima || 30) + 1), ...buildAnimaGainUpdate(npcLive, 1) });
  }
  if (card.id === "tome_anima_house") {
    await updatePlayer(code, NPC_IDX, { anima: Math.min(50, (npcLive.anima || 30) + 2), ...buildAnimaGainUpdate(npcLive, 2) });
  }
  if (card.id === "tome_stars_and_skies") {
    await updatePlayer(code, NPC_IDX, { anima: Math.min(50, (npcLive.anima || 30) + 5), ...buildAnimaGainUpdate(npcLive, 5) });
  }

  // ── BUG #2 FIX: Gauntlets of A'zka — bond to a wielder (set wieldedBy) ──
  // Previously the NPC activated this tome but never chose a wielder, so
  // the +2 Lyt buff (read from tome.wieldedBy in Shatterfield.getEffectiveLyt
  // and in this file's effLytPure) never applied. Pick the NPC's strongest
  // standing being and write wieldedBy onto THIS tome slot, matching the
  // exact shape Shatterfield reads: { wieldedBy: <esclingSlotIdx> }.
  // Face-up, persistent (ATV) tome — it stays bonded for the duel.
  if (card.id === "tome_gauntlets_azka") {
    const myEsclings = newField.esclings || [];
    let bestIdx = -1;
    let bestLyt = -1;
    for (let e = 0; e < myEsclings.length; e++) {
      const s = myEsclings[e];
      if (!s?.escendant || s.fallen) continue;
      const lyt = getCard(s.escendant)?.lyt || 0;
      if (lyt > bestLyt) { bestLyt = lyt; bestIdx = e; }
    }
    if (bestIdx >= 0) {
      tomes[slotIdx] = { ...tomes[slotIdx], wieldedBy: bestIdx };
      newField.tomes = tomes;
      await updatePlayer(code, NPC_IDX, { [`field/tomes/${slotIdx}/wieldedBy`]: bestIdx });
    }
  }

  // Bug G fix: Solomon Godfry's Manslaughter — symmetric forced ruin.
  // The NPC self-ruin operates on the caller's field draft (sync fix).
  if (card.id === "tome_solomon_godfry") {
    await applySolomonGodfryEffect(code, newField);
  }

  // SSG — coin flip: win = draw a card (from the caller's locals)
  const isSSG = SSG_TOMES.includes(card.id) || card.ssg;
  if (isSSG && Math.random() > 0.5) {
    const pool = Object.keys(grimoire || {}).filter(id => (grimoire[id] || 0) > 0);
    if (pool.length > 0) {
      const drawn = pickRandom(pool);
      const newGrim = { ...grimoire };
      if (newGrim[drawn] > 1) newGrim[drawn]--; else delete newGrim[drawn];
      hand = [...hand, drawn];
      grimoire = newGrim;
      await updatePlayer(code, NPC_IDX, { hand, grimoire });
    }
  }

  return { hand, grimoire };
}

// ── Flip ONE waiting tome face-up. ──
// THIS IS THE TOME ACTIVATION MOMENT — chao (and anima) is paid here.
//
// Rulebook (Rulebook.js line 300): "Place Tomes face-down on your 3
// Tome Rifts. Always free. Activation cost paid only when used."
// Flipping face-up == using == cost is paid.
//
// Universal rules: same pattern as player-side handlePlayTome
// (Shatterfield line 5354) — spendChao/spendAnima at activation.
//
// Policy (caP'n's ruling): if a tome can't afford its cost this turn,
// SKIP and try the next face-down tome. Don't block the whole turn,
// don't fire it for free. The skipped tome stays face-down and can
// try again next turn.
//
// Bug A fix: non-persistent tomes ruin to necropolis after firing
// (mirrors player-side handlePlayTome line 5372-5373).
//
// AWAKENING: the tome's simple effects now fire ON THE FLIP via
// applyNpcTomeFireEffects (see above) — hand/grimoire thread through.
async function flipOneTome(code, field, turn, hand, grimoire) {
  const tomes = riftArray(field.tomes, 3); // Firebase hole guard

  for (let i = 0; i < tomes.length; i++) {
    const t = tomes[i];
    if (!t?.id || !t.faceDown) continue;
    const card = getCard(t.id);
    if (!card) continue;
    const waited = turn - (t.placedTurn || 0);
    if (waited < 1) continue;

    const cost = card.chaoCost || 0;
    const animaCost = card.animaCost || 0;

    // Pre-check both costs against fresh Firebase before spending either.
    // Prevents the half-paid-state edge case where chao deducts but anima
    // gate fails afterward.
    const m = await getMatch(code);
    const npcLive = m?.players?.[NPC_IDX] || {};
    if ((npcLive.chao  ?? 0) < cost)      continue;
    if ((npcLive.anima ?? 0) < animaCost) continue;
    if (cost > (npcLive.maxChao || 3))    continue;

    // Pay
    if (!(await npcSpendChao(code, cost)))      continue;
    if (!(await npcSpendAnima(code, animaCost))) continue;

    // Flip face-up + mark cooldown anchor
    const newTomes = [...tomes];
    newTomes[i] = { ...t, faceDown: false, lastUsedTurn: turn };
    const newField = { ...field, tomes: newTomes };
    await updatePlayer(code, NPC_IDX, { field: newField });

    // ── RESPONSE WINDOW — the activation is DECLARED before its
    // effect resolves. Nihil voids the effect; Omnia ruins the tome
    // outright. Fare stays paid either way (canon: paid once). A
    // nullified card's normal lifecycle continues — it was activated,
    // its effect was voided, non-persistent tomes still ruin after use.
    // COUNTER HIERARCHY (caP'n ruling): a LATE counter (L1/L2 vs this
    // L3+ activation) cannot pre-empt — the effect resolves first,
    // then the card must leave the field, persistent or not.
    let lateCounter = false;
    {
      const rw = await requestWindow({ kind: "ACTIVATION", cardId: t.id });
      if (rw?.counter && rw?.late) {
        lateCounter = true; // effect proceeds below; eviction after
      } else if (rw?.counter === "omnia") {
        await ruinNpcTome(code, i, card.id);
        newField.tomes[i] = null;
        await sleep(TOME_INTERRUPT);
        return { field: newField, flipped: true, hand, grimoire };
      } else if (rw?.counter === "nihil") {
        if (!isTomePersistent(card)) {
          await ruinNpcTome(code, i, card.id);
          newField.tomes[i] = null;
        }
        await sleep(TOME_INTERRUPT);
        return { field: newField, flipped: true, hand, grimoire };
      }
    }

    // AWAKENING: fire the tome's effects at the activation moment.
    // (Solomon fires here and ONLY here — it has no effectTag and is
    // not FIELD_PERSISTENT, so activateOneTome never sees it.)
    const fx = await applyNpcTomeFireEffects(code, card, newField, newTomes, i, hand, grimoire);
    hand = fx.hand; grimoire = fx.grimoire;

    // Bug A fix: non-persistent tomes ruin to necropolis after firing.
    // Counter Hierarchy: a late-countered tome ALWAYS leaves the field.
    if (!isTomePersistent(card) || lateCounter) {
      await ruinNpcTome(code, i, card.id);
      newField.tomes[i] = null;
    }

    await sleep(TOME_INTERRUPT);
    return { field: newField, flipped: true, hand, grimoire };
  }

  return { field, flipped: false, hand, grimoire };
}

// ── Place ONE tome face-down into one empty tome slot. ──
// `chooserMode`: "Beb" / "Champion" / "Lord-aggro" / "Lord-survival" / "Lord-balance"
async function placeOneTome(code, hand, field, turn, chooserMode) {
  if (turn === 0) return { hand, field, placed: false }; // never on turn 0

  // riftArray also fixes a latent miss: a Firebase-shortened array from
  // {0:tome} is length 1, so `findIndex(t => t === null)` returned -1
  // and the NPC could never place a second tome.
  const tomes = riftArray(field.tomes, 3);
  const slotIdx = tomes.findIndex(t => t === null);
  if (slotIdx === -1) return { hand, field, placed: false };

  const tomesInHand = hand.map((id, idx) => ({ card: getCard(id), idx }))
    .filter(({ card }) => card?.type === "Tome");
  if (tomesInHand.length === 0) return { hand, field, placed: false };

  let chosen;
  if (chooserMode === "Lord-survival") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Construction")
      || tomesInHand.find(({ card }) => card?.ilk === "Nihil")
      || tomesInHand[0];
  } else if (chooserMode === "Lord-aggro") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Omnia")
      || tomesInHand.find(({ card }) => card?.ilk === "Treasure")
      || tomesInHand[0];
  } else if (chooserMode === "Lord-balance") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Construction")
      || tomesInHand.sort((a, b) => (b.card?.level || 0) - (a.card?.level || 0))[0];
  } else if (chooserMode === "Champion") {
    // Champion: prefer highest level
    chosen = tomesInHand.sort((a, b) => (b.card?.level || 0) - (a.card?.level || 0))[0];
  } else {
    // Beb: random
    chosen = pickRandom(tomesInHand);
  }
  if (!chosen) return { hand, field, placed: false };

  const newTomes = [...tomes];
  newTomes[slotIdx] = { id: hand[chosen.idx], faceDown: true, placedTurn: turn };
  const newHand = [...hand];
  newHand.splice(chosen.idx, 1);
  const newField = { ...field, tomes: newTomes };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, placed: true };
}

// ── Activate ONE eligible tome (RE-ACTIVATION of already-face-up tome). ──
// First-time activations happen in flipOneTome (above) — that's where
// face-down → face-up + initial cost payment lives. THIS function handles
// re-activation of persistent tomes (OPT/OP2/OP3/ATV) that have already
// been flipped face-up on a previous turn.
//
// Universal rules: gate via fresh Firebase read (npcSpendChao), no local
// state mirrors. Mirrors player-side "Activate Again" path (Shatterfield
// line 6132-6133).
//
// Returns { field, activated, hand, grimoire }. Loops in turn runner:
// keeps calling until no eligible tome remains.
async function activateOneTome(code, field, turn, difficulty, hand, grimoire) {
  const tomes = riftArray(field.tomes, 3); // Firebase hole guard (spread on a sparse OBJECT throws)

  for (let i = 0; i < tomes.length; i++) {
    const tome = tomes[i];
    if (!tome?.id || tome.faceDown) continue;
    if (tome._activatedThisCall) continue; // guard against re-activation in same turn
    const card = getCard(tome.id);
    if (!card) continue;

    // Cooldown gate (OP2 = every 2 turns, OP3 = every 3 turns, default = 1)
    const cooldown = card.effectTag === "OP2" ? 2 : card.effectTag === "OP3" ? 3 : 1;
    const lastUsed = tome.lastUsedTurn || 0;
    if (turn - lastUsed < cooldown) continue;

    const cost      = card.chaoCost  || 0;
    const animaCost = card.animaCost || 0;

    // Pre-check costs against fresh Firebase
    const m = await getMatch(code);
    const npcLive = m?.players?.[NPC_IDX] || {};
    if ((npcLive.chao  ?? 0) < cost)      continue;
    if ((npcLive.anima ?? 0) < animaCost) continue;
    if (cost > (npcLive.maxChao || 3))    continue;

    const isGood       = GOOD_TOMES.includes(card.id);
    const isAggressive = AGGRESSIVE_TOMES.includes(card.id);
    const isSSG        = SSG_TOMES.includes(card.id) || card.ssg;

    // ── AGGRESSION BUMP (carry-over from prior session) ──
    // Beb: was random>0.35 gate on good — now always activates good tomes.
    // Champion: was random>0.15 gate — now always activates good/aggressive.
    // Lord: unchanged (already activates everything).
    let shouldActivate = false;
    if (difficulty === "Beb")      shouldActivate = isGood;
    if (difficulty === "Champion") shouldActivate = isGood || isAggressive;
    if (difficulty === "Lord")     shouldActivate = isGood || isAggressive || isSSG;
    if (!shouldActivate) continue;

    // Pay
    if (!(await npcSpendChao(code, cost)))      continue;
    if (!(await npcSpendAnima(code, animaCost))) continue;

    // Mark cooldown anchor
    tomes[i] = { ...tome, lastUsedTurn: turn };
    const newField = { ...field, tomes };
    await updatePlayer(code, NPC_IDX, { field: newField });

    // ── RESPONSE WINDOW — re-activation is still an activation, still
    // declared. Omnia ruins even a persistent tome mid-cooldown; Nihil
    // voids this firing (cooldown anchor already stamped — the turn's
    // use is spent, exactly as if the effect fizzled).
    let lateCounter = false;
    {
      const rw = await requestWindow({ kind: "ACTIVATION", cardId: tome.id });
      if (rw?.counter && rw?.late) {
        // COUNTER HIERARCHY (caP'n ruling): L1/L2 counter vs this L3+
        // activation is LATE — effect resolves first, then eviction.
        lateCounter = true;
      } else if (rw?.counter === "omnia") {
        await ruinNpcTome(code, i, card.id);
        newField.tomes[i] = null;
        await sleep(TOME_INTERRUPT);
        return { field: newField, activated: true, hand, grimoire };
      } else if (rw?.counter === "nihil") {
        await sleep(TOME_INTERRUPT);
        return { field: newField, activated: true, hand, grimoire };
      }
    }

    // AWAKENING: apply effects via the shared fire-effects helper
    // (each write individually visible; hand/grimoire kept in sync
    // with the caller's locals — the old inline SSG draw desynced).
    const fx = await applyNpcTomeFireEffects(code, card, newField, tomes, i, hand, grimoire);
    hand = fx.hand; grimoire = fx.grimoire;

    // Bug A fix: non-persistent tomes ruin to necropolis after firing.
    // (Persistent tomes — OPT/OP2/OP3/ATV/specific-ids — stay on field
    // for re-activation.) Counter Hierarchy: a late-countered tome
    // ALWAYS leaves the field, persistence be damned.
    if (!isTomePersistent(card) || lateCounter) {
      await ruinNpcTome(code, i, card.id);
      newField.tomes[i] = null;
    }

    await sleep(TOME_INTERRUPT);
    return { field: newField, activated: true, hand, grimoire };
  }

  return { field, activated: false, hand, grimoire };
}

// ═════════════════════════════════════════════════════════════
// ATTACKS — per-attack, defensive-state-aware (AWAKENING).
// Adds POST_RUIN_DELAY when player being is destroyed (descent visibility).
//
// What changed in the Awakening wave:
//   • getRequiredTargetNpc — the PLAYER's being-level mustBeRuinedFirst
//     (Quasar) is honored FIRST, then the D'uhmdumn tome (canon order,
//     mirrors Shatterfield.getRequiredTarget). Attacks are DIRECTED at
//     the required target, never skipped.
//   • strikeBeing — every strike resolves the target's defensive states
//     in the player-side order: fallen (impervious) → intangible
//     (pass-through, flag consumed) → nullifyNextAttack (consumed) →
//     reflectNextAttack (Fate Maker: the attacker's Lyt dims the NPC's
//     OWN Anima — the NPC can genuinely lose mid-attack-phase) →
//     effective-Lyf combat math (ruin / fracture / chip).
//   • EFFECTIVE stats everywhere — the NPC attacks with its trained
//     buffs (Gauntlets, Apocalypse Now, Premier auras) and respects the
//     player's (Diamond Skin, Cosmic Casbah).
//   • Offensive soul flags — extraAttack (second swing), extra-
//     AttackAnimaOnly (second swing, Anima only), canDirectAnima /
//     animaRedirectAll (Lyt Attack the Anima past standing beings —
//     still blocked by any must-ruin-first requirement, mirroring the
//     player-side anima-attack gate), dimAnimaOnAttack, spectralClaws,
//     invFangsBuff (consumed on the anima-dimming swing).
//   • Sweet Dreams parity — u_xulan_lord on any player being makes the
//     NPC dim its own Anima by 1 per Lyt Attack, same as a human.
//   • Prophecy parity — player Anima dims write totalAnimaLost /
//     animaLostThisTurn trackers (buildAnimaLossUpdate semantics).
//
// Returns { liveField, liveAnima, killed, npcDied }. npcDied means the
// match ENDED with the player winning (reflect / Sweet Dreams) — the
// runner must stop immediately, no end-turn writes.
// ═════════════════════════════════════════════════════════════
// DEPTHS BRAIN additions (all decision-quality, all inside the same
// rulebook):
//   • LETHAL FOCUS (Lord, skill ≥ 0.82) — canKillNow is computed at
//     TURN START, before ascends/training/souls raise the Lyt. If the
//     lane is clear and the AVAILABLE Lyt is lethal at attack time,
//     the plan is discarded and every attacker goes face. A guaranteed
//     kill is never traded for a being.
//   • PROPHECY DENIAL (Lord, skill ≥ 0.95) — the player being closest
//     to completing THEIR prophecy is planned against first.
//   • Temperament — Champion animaHunger prefers a trained redirect
//     over chip damage; Beb's throat-lust runs through chance().
async function doAttacks(code, esclings, player, difficulty, npcOwnField = null, matchTurnParam = null, brain = null) {
  let liveField = JSON.parse(JSON.stringify(player.field || {}));
  liveField.tomes = riftArray(liveField.tomes, 3); // Firebase hole guard — attackRuinFirstTome indexes this
  let liveAnima = player.anima || 30;
  let liveNec   = [...(player.necropolis || [])];
  let killed    = false;
  let npcDied   = false;

  // ── BUG #2 FIX support: read the NPC's OWN field once, for Gauntlets ──
  // doAttacks receives the NPC's esclings array but not its tome rift, so
  // fetch the NPC's live field to resolve the Gauntlets bond when scoring
  // attacker Lyt. Built once per attack phase (cheap) and reused below.
  const _npcMatch = await getMatch(code);
  const _npcField = _npcMatch?.players?.[NPC_IDX]?.field || { esclings, tomes: [] };
  const effLyt = (slotIdx) => effLytPure(esclings[slotIdx], slotIdx, _npcField, liveField);
  const matchTurn = matchTurnParam ?? (_npcMatch?.turn || 0);

  // ── Sweet Dreams parity (u_xulan_lord on ANY player being) ──
  const playerHasSweetDreams = (liveField.esclings || []).some(s =>
    (s?.soul || []).some(id => id === "u_xulan_lord"));

  // ── Player Anima strikes — with prophecy-tracker parity ──
  const lostBaseTotal = player.totalAnimaLost || 0;
  const lostBaseTurn  = player.animaLostThisTurn || 0;
  let   lostAccum     = 0;
  const strikePlayerAnima = async (amt, allowWindow = true) => {
    if (amt <= 0) return;
    // ── RESPONSE WINDOW — a direct Anima strike is declared. Riders
    // (dimAnimaOnAttack / spectralClaws / invFangs) pass allowWindow
    // false: they are consequences of an already-windowed attack, not
    // fresh declarations — one attack, one window, never two prompts.
    if (allowWindow) {
      const rw = await requestWindow({ kind: "ANIMA_ATTACK", lyt: amt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveAnima = fp.anima ?? liveAnima;
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
        }
      }
    }
    lostAccum += amt;
    liveAnima = Math.max(0, liveAnima - amt);
    await updatePlayer(code, 0, {
      anima: liveAnima,
      totalAnimaLost: lostBaseTotal + lostAccum,
      animaLostThisTurn: lostBaseTurn + lostAccum,
    });
    await sleep(KILL_SHOT_DELAY);
    if (liveAnima <= 0) killed = true;
  };

  const liveTargets = () => {
    const slots = liveField.esclings || [];
    // R25: Fallen beings are impervious to Lyt attacks — never targets.
    const attackable = slots.map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant && !s.fallen);
    const vuln     = attackable.filter(({ s }) => isVulnerable(s, liveField));
    const standing = attackable.filter(({ s }) => !isVulnerable(s, liveField));
    return { vuln, standing };
  };

  // ── R26 (canon, July 2026): Fallen beings are BYPASSABLE immediately ──
  // A being that descended — even this very turn — does NOT block Anima
  // attacks. Ruin the L3, walk past the L2 it left behind, dim Anima.
  // Only standing (non-fallen) beings block.
  const animaClear = () => (liveField.esclings || []).every(s => !s?.escendant || s?.fallen);

  // ── Taunt attack (must-ruin-first, July 2026 canon) ──
  // While the PLAYER has a face-up Chao D'uhmdumn, every NPC Lyt attack
  // is DIRECTED AT THE TOME (not skipped). Resolution mirrors the player
  // side: Lord's nullifyFirstRuin soaks the first hit of any kind;
  // Lyt >= current Lyf ruins the tome to Necropolis; Lyt < Lyf chips.
  // Returns true if the attacker spent its attack on the tome.
  const attackRuinFirstTome = async (attackerLyt) => {
    // ── RESPONSE WINDOW — the tome-directed strike is declared. The
    // re-read happens BEFORE deriving the required target: a tome
    // flipped or state changed mid-window feeds the derivation fresh.
    {
      const rw = await requestWindow({ kind: "LYT_ATTACK_TOME", lyt: attackerLyt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
        }
      }
    }
    const req = getRuinFirstTome(liveField);
    if (!req) return false;
    const t = liveField.tomes[req.slotIdx];
    if (t?.nullifyFirstRuin) {
      // Lord soaks the first hit — flag consumed, tome untouched.
      liveField.tomes[req.slotIdx] = { ...t, nullifyFirstRuin: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return true;
    }
    const card = getCard(req.id);
    const tomeLyf = (t?.currentLyf !== null && t?.currentLyf !== undefined) ? t.currentLyf : (card?.lyf ?? 0);
    if (attackerLyt >= tomeLyf) {
      liveField.tomes[req.slotIdx] = null;
      liveNec.push(req.id);
      await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
      await sleep(POST_RUIN_DELAY);
    } else {
      liveField.tomes[req.slotIdx] = { ...t, currentLyf: Math.max(0, tomeLyf - attackerLyt) };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
    }
    return true;
  };

  // ── AWAKENING: defensive-state-aware strike on a player being ──
  // Player-side resolution order mirrored exactly (Shatterfield ~6740+):
  // fallen → intangible → nullify → reflect → effective-Lyf math.
  // Returns "none" | "spent" | "ruined" | "fractured" | "chipped" | "npcDied".
  const strikeBeing = async (targetIdx, aLyt) => {
    // ── RESPONSE WINDOW (Rulez v2 amendment, July 2026) ──────────
    // The strike is DECLARED, not yet resolved. The defender may fire
    // a legal interrupt; if they do, re-read their live state so
    // freshly-set defensive flags (intangible / nullify / reflect /
    // buffs) count against THIS strike. Every prior mid-phase write
    // is already persisted, so Firebase is the one truth to re-read.
    {
      const rw = await requestWindow({ kind: "LYT_ATTACK", targetIdx, lyt: aLyt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
          liveAnima = fp.anima ?? liveAnima;
        }
      }
    }
    const t = liveField.esclings[targetIdx];
    if (!t?.escendant) return "none";
    if (t.fallen) return "none"; // R25 — impervious to Lyt
    if (t.intangible) {
      // Attack passes through — being unharmed, flag consumed, attack spent.
      liveField.esclings[targetIdx] = { ...t, intangible: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "spent";
    }
    if (t.nullifyNextAttack) {
      liveField.esclings[targetIdx] = { ...t, nullifyNextAttack: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "spent";
    }
    if (t.reflectNextAttack) {
      // Fate Maker — reflected onto the NPC's OWN Anima. Flag consumed.
      liveField.esclings[targetIdx] = { ...t, reflectNextAttack: false };
      await updatePlayer(code, 0, { field: liveField });
      const died = await dimNpcAnima(code, aLyt);
      await sleep(ACTION_DELAY);
      if (died) { npcDied = true; return "npcDied"; }
      return "spent";
    }
    const defLyf = effLyfPure(t, liveField); // EFFECTIVE Lyf — player buffs count
    if (defLyf === 0) {
      // Universal ruin semantics — descend or full-ruin via helper.
      descendPlayerSlot(liveField, liveNec, targetIdx, matchTurn);
      await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
      await sleep(POST_RUIN_DELAY);
      return "ruined";
    }
    if (aLyt >= defLyf) {
      liveField.esclings[targetIdx] = { ...t, currentLyf: 0 };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "fractured";
    }
    // Chip — mirrors the player-side write (effective Lyf minus Lyt).
    liveField.esclings[targetIdx] = { ...t, currentLyf: Math.max(0, defLyf - aLyt) };
    await updatePlayer(code, 0, { field: liveField });
    await sleep(ACTION_DELAY);
    return "chipped";
  };

  // ── Post-attack riders for the attacker that just swung ──
  // dimAnimaOnAttack fires on every Lyt Attack; spectralClaws and
  // invFangsBuff fire only when the attack itself dimmed Anima
  // (invFangsBuff is consumed). Sweet Dreams self-dim always applies.
  const afterAttackRiders = async (attackerIdx, struckAnima) => {
    if (npcDied) return;
    const s = esclings[attackerIdx];
    let dim = 0;
    if (s?.dimAnimaOnAttack) dim += s.dimAnimaOnAttack;
    if (struckAnima && s?.spectralClaws) dim += 1;
    if (struckAnima && s?.invFangsBuff) {
      dim += 2;
      esclings[attackerIdx] = { ...esclings[attackerIdx], invFangsBuff: false };
    }
    if (dim > 0 && !killed) await strikePlayerAnima(dim, false); // rider — already windowed
    if (playerHasSweetDreams) {
      const died = await dimNpcAnima(code, 1);
      if (died) npcDied = true;
    }
  };

  // LORD: pre-compute attack plan (AWAKENING: effective values, no
  // planning against Fallen beings — R25 makes those attacks dead).
  let lordPlan = null;
  if (difficulty === "Lord") {
    const myAttackers = esclings
      .map((s, i) => ({ s, i, lyt: effLyt(i) }))
      .filter(({ s }) => s?.escendant && !s.attackedThisTurn && !s.fallen);

    // ── LETHAL FOCUS (DEPTHS BRAIN, skill ≥ 0.82) ──
    // canKillNow was measured at turn start, BEFORE this turn's
    // ascends/training/souls raised the Lyt. Re-measure here: if the
    // lane is clear and the available Lyt is lethal RIGHT NOW, plan
    // nothing — every attacker goes face. Base Lords can still get
    // distracted by a juicy combo while holding a winning hand; the
    // Aenarchist band never does.
    const availLyt = myAttackers.reduce((sum, a) => sum + (a.lyt || 0), 0);
    if ((brain?.skill ?? 0) >= SKILL_LETHAL_FOCUS && animaClear() && availLyt >= liveAnima) {
      lordPlan = { soloKill: [], comboPlan: [], myAttackers, faceAll: true, denialIdx: -1 };
    } else {

    // ── PROPHECY DENIAL (DEPTHS BRAIN, skill ≥ 0.95) ──
    // Sort the denial target to the front of the planning queue so the
    // soloKill / comboPlan assignments claim it first.
    const denialIdx = (brain?.skill ?? 0) >= SKILL_DENIAL ? findDenialTarget(player) : -1;

    const oppBeings = (liveField.esclings || [])
      .map((s, i) => ({ s, i, lyf: effLyfPure(s, liveField) }))
      .filter(({ s }) => s?.escendant && !s.fallen);
    if (denialIdx >= 0) {
      oppBeings.sort((a, b) => (b.i === denialIdx ? 1 : 0) - (a.i === denialIdx ? 1 : 0));
    }

    const comboPlan = [];
    const soloKill  = [];

    oppBeings.forEach(opp => {
      if (opp.lyf === 0) {
        const attacker = myAttackers.find(a => !soloKill.find(p => p.attackerIdx === a.i));
        if (attacker) soloKill.push({ targetIdx: opp.i, attackerIdx: attacker.i });
        return;
      }
      const defCard = getCard(opp.s.escendant);
      myAttackers.forEach(atk => {
        if (soloKill.find(p => p.attackerIdx === atk.i)) return;
        if (atk.lyt >= opp.lyf && defCard?.level === 1) {
          soloKill.push({ targetIdx: opp.i, attackerIdx: atk.i, twoHit: true });
        }
      });
      for (let a = 0; a < myAttackers.length; a++) {
        for (let b = 0; b < myAttackers.length; b++) {
          if (a === b) continue;
          if (comboPlan.find(p => p.fracturerIdx === myAttackers[a].i || p.ruinerIdx === myAttackers[b].i)) continue;
          if (soloKill.find(p => p.attackerIdx === myAttackers[a].i || p.attackerIdx === myAttackers[b].i)) continue;
          const fractures = myAttackers[a].lyt >= opp.lyf;
          const ruins     = myAttackers[b].lyt >= 0;
          if (fractures && ruins) {
            comboPlan.push({ targetIdx: opp.i, fracturerIdx: myAttackers[a].i, ruinerIdx: myAttackers[b].i });
            break;
          }
        }
        if (comboPlan.find(p => p.targetIdx === opp.i)) break;
      }
    });

    lordPlan = { soloKill, comboPlan, myAttackers, faceAll: false, denialIdx };
    }
  }

  for (let i = 0; i < esclings.length; i++) {
    const slot = esclings[i];
    if (!slot?.escendant || slot.attackedThisTurn || slot.fallen) continue;
    const attacker = getCard(slot.escendant);
    if (!attacker || !attacker.lyt) continue;

    // ── AWAKENING: extra-swing soul flags ──
    const hasExtra        = !!(slot.extraAttack || slot.extraAttackAnimaOnly);
    const animaOnlySecond = !!slot.extraAttackAnimaOnly && !slot.extraAttack;
    const swings          = hasExtra ? 2 : 1;
    const canRedirect     = !!(slot.canDirectAnima || slot.animaRedirectAll);

    let acted = false;
    let attackerRuined = false;

    for (let swing = 0; swing < swings; swing++) {
      if (killed || npcDied) break;
      if (swing === 1 && !acted) break; // first swing found nothing — no second
      // BUG #2 FIX: effective attack value includes the Gauntlets +2 bond.
      const aLyt = effLyt(i);
      const animaOnly = swing === 1 && animaOnlySecond;

      // ── Must-target resolution — being MRF first, then tome (canon) ──
      const req = getRequiredTargetNpc(liveField);
      if (req?.kind === "tome") {
        await attackRuinFirstTome(aLyt);
        acted = true;
        await afterAttackRiders(i, false);
        continue;
      }
      if (req?.kind === "escling") {
        if (animaOnly) break; // anima-only swing has no legal target under a taunt
        const r = await strikeBeing(req.slotIdx, aLyt);
        if (r === "npcDied") break;
        acted = true;
        await afterAttackRiders(i, false);
        continue;
      }

      // ── None More Than I — the PLAYER's Chaos Lord aura (July 2026) ──
      // Symmetric with the player side: an NPC attacker with Lyt > 10 that
      // Lyt-attacks while the player has Chaos Dragon Lord + p_chaos_dragon_3
      // is instantly ruined (universal semantics — descends if a lower level
      // waits in its soul, otherwise full ruin with soul to Necropolis).
      const playerHasNoneMoreThanI = (liveField.esclings || []).some(s => {
        const c = getCard(s?.escendant);
        return c?.id === "chaos_dragon_lord" && (s?.soul || []).some(id => id === "p_chaos_dragon_3");
      });
      if (playerHasNoneMoreThanI && aLyt > 10) {
        const npcMatchNow = await getMatch(code);
        const npcNec = [...((npcMatchNow?.players?.[NPC_IDX]?.necropolis) || [])];
        // Mutate the caller's OWN field object (npcOwnField.esclings IS the
        // shared `esclings` array) so the descent + any wielded-Treasure
        // cascade survive the caller's full-field write at turn end.
        const npcFieldDraft = npcOwnField || { esclings, tomes: [] };
        descendPlayerSlot(npcFieldDraft, npcNec, i, matchTurn);
        await updatePlayer(code, NPC_IDX, { field: npcFieldDraft, necropolis: npcNec });
        await sleep(POST_RUIN_DELAY);
        attackerRuined = true;
        break;
      }

      // ── Anima-only second swing (Restless) ──
      if (animaOnly) {
        if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = true;
          await afterAttackRiders(i, true);
        }
        break;
      }

      const { vuln, standing } = liveTargets();
      const allTargets = [...vuln, ...standing];

      if (difficulty === "Lord" && lordPlan) {
        // ── LETHAL FOCUS — the plan IS the face. Nothing else exists.
        if (lordPlan.faceAll) {
          if (animaClear() || canRedirect) {
            await strikePlayerAnima(aLyt);
            acted = true;
            await afterAttackRiders(i, true);
            if (killed) break;
          }
          continue;
        }

        const soloTarget = swing === 0 ? lordPlan.soloKill.find(p => p.attackerIdx === i) : null;
        const fracTarget = swing === 0 ? lordPlan.comboPlan.find(p => p.fracturerIdx === i) : null;
        const ruinTarget = swing === 0 ? lordPlan.comboPlan.find(p => p.ruinerIdx === i) : null;

        if (soloTarget) {
          const t = liveField.esclings[soloTarget.targetIdx];
          if (t?.escendant && (isVulnerable(t, liveField) || (soloTarget.twoHit && aLyt >= effLyfPure(t, liveField)))) {
            const r = await strikeBeing(soloTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            // twoHit — fracture then ruin in the same attack (pre-existing
            // Lord behavior, preserved; defenses above intercept first).
            if (r === "fractured" && soloTarget.twoHit) {
              descendPlayerSlot(liveField, liveNec, soloTarget.targetIdx, matchTurn);
              await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
              await sleep(POST_RUIN_DELAY);
            }
            await afterAttackRiders(i, false);
          }
          acted = true;
          continue;
        }

        if (fracTarget) {
          const t = liveField.esclings[fracTarget.targetIdx];
          if (t?.escendant && !isVulnerable(t, liveField)) {
            const r = await strikeBeing(fracTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            await afterAttackRiders(i, false);
          }
          acted = true;
          continue;
        }

        if (ruinTarget) {
          const t = liveField.esclings[ruinTarget.targetIdx];
          if (t?.escendant && isVulnerable(t, liveField) && !t.fallen) {
            const r = await strikeBeing(ruinTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            await afterAttackRiders(i, false);
          }
          acted = true;
          continue;
        }

        // No planned target — Lord strikes Anima if clear OR via redirect
        if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = true;
          await afterAttackRiders(i, true);
          if (killed) break;
        } else if (standing.length > 0) {
          // ── AGGRESSION BUMP ──
          // Lord previously held back if no kill plan. Now it fractures the
          // closest-to-fracture standing being to set up future combos.
          // PROPHECY DENIAL: if the player's prophecy piece is standing
          // and fracturable, it eats the swing instead.
          let target = standing.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
          if (lordPlan.denialIdx >= 0) {
            const den = standing.find(x => x.i === lordPlan.denialIdx);
            if (den && aLyt >= effLyfPure(den.s, liveField)) target = den;
          }
          if (aLyt >= effLyfPure(target.s, liveField)) {
            const r = await strikeBeing(target.i, aLyt);
            if (r === "npcDied") break;
            await afterAttackRiders(i, false);
          }
          // If can't fracture, skip — Lord still values not-wasting on chip
          // damage. The attack is spent either way (matches prior behavior).
          acted = true;
        }

      } else if (allTargets.length > 0) {
        // ── BEB / CHAMPION ──
        let target = null;

        if (difficulty === "Beb") {
          // Beb: chaotic — sometimes goes for the throat when redirect is
          // live, otherwise a random legal target. DEPTHS BRAIN: the
          // throat-lust runs through the temperament gate (Reckless +,
          // Patient −, Cold always).
          if (canRedirect && chance(0.5, brain)) target = null;
          else target = pickRandom(allTargets);
        } else {
          // Champion: ruin vuln first, then fracture if possible, then
          // redirect if trained for it, then chip the biggest threat.
          // DEPTHS BRAIN: animaHunger temperaments (Reckless/Cold) take
          // a trained redirect BEFORE hunting fractures — pressure over
          // patience.
          if (vuln.length > 0) {
            target = vuln[0];
          } else if (canRedirect && brain?.t?.animaHunger) {
            target = null;
          } else {
            const canFracture = standing.filter(({ s }) => aLyt >= effLyfPure(s, liveField));
            if (canFracture.length > 0) {
              target = canFracture.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
            } else if (canRedirect) {
              target = null;
            } else if (standing.length > 0) {
              // ── AGGRESSION BUMP ──
              // Champion previously held back if it couldn't fracture. Now it
              // chips the highest-Lyt threat to apply pressure.
              target = standing.sort((a, b) =>
                (getCard(b.s.escendant)?.lyt || 0) - (getCard(a.s.escendant)?.lyt || 0))[0];
            }
          }
        }

        if (target) {
          const r = await strikeBeing(target.i, aLyt);
          if (r === "npcDied") break;
          acted = true;
          await afterAttackRiders(i, false);
        } else if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = true;
          await afterAttackRiders(i, true);
          if (killed) break;
        }

      } else if (animaClear() || canRedirect) {
        await strikePlayerAnima(aLyt);
        acted = true;
        await afterAttackRiders(i, true);
        if (killed) break;
      }
    }

    // Attacker was ruined mid-phase (None More Than I) — slot already
    // rewritten by descendPlayerSlot; never re-mark it.
    if (attackerRuined) { if (killed || npcDied) break; continue; }

    if (acted) {
      esclings[i] = {
        ...esclings[i],
        attackedThisTurn: true,
        extraAttack: false,          // consumed (whether or not the second swing landed)
        extraAttackAnimaOnly: false, // consumed
      };
    }
    if (killed || npcDied) break;
  }

  // ── Rulez v2 (July 2026): ESCLINGS CAN LYT ATTACK ──────────────
  // Bare Esclings (no Escendant transcended on top) may Lyt Attack with
  // their PRINTED Lyt — Escendants ONLY, NEVER Anima. That prohibition
  // is absolute: even with the player's board fully Fallen or empty, an
  // Escling has no path to the Anima. Mirrors the player-side
  // handleStartAttackEscling / handleAttackTargetEscling pair.
  // Archetype appetite: Beb swings whenever able (chaos with a
  // rulebook); Champion/Lord swing only when it accomplishes something
  // (ruin a vulnerable being or land a fracture).
  for (let i = 0; i < esclings.length; i++) {
    if (killed || npcDied) break;
    const slot = esclings[i];
    if (!slot?.escling || slot?.escendant || slot.attackedThisTurn) continue;
    const eLyt = getCard(slot.escling)?.lyt || 0;
    if (eLyt <= 0) continue;

    const { vuln, standing } = liveTargets();
    if (vuln.length === 0 && standing.length === 0) continue; // no beings — Anima is off-limits to Esclings

    // Taunt tome — Escling attacks are directed at it, same as any Lyt Attack.
    const req = getRequiredTargetNpc(liveField);
    if (req?.kind === "tome") {
      await attackRuinFirstTome(eLyt);
      esclings[i] = { ...slot, attackedThisTurn: true };
      await afterAttackRiders(i, false);
      continue;
    }

    let target = null;
    if (req?.kind === "escling") {
      target = { i: req.slotIdx };
    } else if (difficulty === "Lord" || difficulty === "Champion") {
      if (vuln.length > 0) target = vuln[0];
      else {
        const canFracture = standing.filter(({ s }) => eLyt >= effLyfPure(s, liveField));
        if (canFracture.length > 0) {
          target = canFracture.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
        }
      }
    } else {
      // Beb (and any unknown archetype) — a random legal target, every time.
      target = pickRandom([...vuln, ...standing]);
    }
    if (!target) continue;

    const r = await strikeBeing(target.i, eLyt);
    if (r === "npcDied") break;
    if (r !== "none") {
      esclings[i] = { ...esclings[i], attackedThisTurn: true };
      await afterAttackRiders(i, false);
    }
    if (killed || npcDied) break;
  }

  return { liveField, liveAnima, killed, npcDied };
}

// ─────────────────────────────────────────────────────────────
// R10 (Rulez v2, July 2026): NPC HAND CAP — draw-then-discard parity
// ─────────────────────────────────────────────────────────────
// Runs at end of turn before the final hand write. If the turn draw or
// effect draws pushed the hand over 8, discard down by value tier:
// dead Tomes first (lowest level), then excess duplicates, then random.
// The NPC is never stuck above the cap.
const NPC_HAND_LIMIT = 8;
function npcPickDiscardIdx(hand) {
  const tomes = hand.map((id, i) => ({ c: getCard(id), i })).filter(x => x.c?.type === "Tome");
  if (tomes.length > 0) return tomes.sort((a, b) => (a.c.level || 1) - (b.c.level || 1))[0].i;
  const seen = {};
  for (let i = 0; i < hand.length; i++) {
    if (seen[hand[i]] !== undefined) return i; // second copy = excess dupe
    seen[hand[i]] = i;
  }
  return Math.floor(Math.random() * hand.length);
}
async function npcEnforceHandCap(code, hand) {
  if (hand.length <= NPC_HAND_LIMIT) return hand;
  const m = await getMatch(code);
  const nec = [...((m?.players?.[NPC_IDX]?.necropolis) || [])];
  const h = [...hand];
  while (h.length > NPC_HAND_LIMIT) {
    const [gone] = h.splice(npcPickDiscardIdx(h), 1);
    nec.push(gone);
  }
  await updatePlayer(code, NPC_IDX, { necropolis: nec });
  return h;
}

// ─────────────────────────────────────────────────────────────
// END OF TURN: reset attacked flags, restore Lyf
// ─────────────────────────────────────────────────────────────
function resetEndOfTurn(esclings) {
  return esclings.map(s => {
    const card = getCard(s?.escendant);
    return {
      ...s,
      attackedThisTurn: false,
      currentLyf: card ? (card.lyf ?? null) : null,
      // Fallen is a ONE-TURN state (canon, July 2026): it clears when the
      // owner's turn ends — the same moment Lyf regenerates. It used to
      // persist forever, leaving descended NPC beings permanently
      // Lyt-immune and permanently unable to attack.
      fallen: false,
      fallenTurn: null,
      // AWAKENING: per-turn soul-effect flags clear at end of turn —
      // extra swings and the all-beings Anima redirect are "this turn"
      // effects. Persistent-until-consumed flags (intangible, nullify,
      // reflect, dimAnimaOnAttack, canDirectAnima, spectralClaws) stay.
      extraAttack: false,
      extraAttackAnimaOnly: false,
      animaRedirectAll: false,
    };
  });
}

// ─────────────────────────────────────────────────────────────
// BUILD NPC PLAYER DATA
// ─────────────────────────────────────────────────────────────
export function buildNPCPlayerData(difficulty, starterDeck) {
  // ── DEPTHS BRAIN: consume the one-shot handoff (if a launcher set
  // one) and stamp it onto the seat-1 object. Firebase note for the
  // future rules-hardening session: `npcSkill` and `temperament` are
  // two new fields on the match player object — whitelist them if the
  // permissive closed-beta match rules ever grow an $extra:false.
  const pendingBrain = consumePendingNpcBrain();
  const brainStamp = {
    npcSkill: clamp01(
      (typeof pendingBrain?.skill === "number") ? pendingBrain.skill
        : (TIER_BASELINE_SKILL[difficulty] ?? 0.5)
    ),
    ...(NPC_TEMPERAMENTS.includes(pendingBrain?.temperament)
      ? { temperament: pendingBrain.temperament } : {}),
  };

  const grimoire = { ...starterDeck.grimoire };
  const luxmoire = { ...starterDeck.luxmoire };

  const hand = [];
  const tempGrimoire = { ...grimoire };
  for (let i = 0; i < 5; i++) {
    const drawn = drawFromPool(tempGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (tempGrimoire[drawn] > 1) tempGrimoire[drawn]--;
      else delete tempGrimoire[drawn];
    }
  }

  const deckCards = Object.keys(grimoire);
  const firstEscendant = ESCENDANTS.find(e => deckCards.includes(e.id));
  const matchingEscling = firstEscendant ? ESCLINGS.find(e => e.ilk === firstEscendant.ilk) : null;
  const defaultEscling = matchingEscling || ESCLINGS[0];

  const esclings = [0, 1, 2].map(i => ({
    id: `npc_slot${i}`,
    escling: defaultEscling.id,
    escendant: null, soul: [], fallen: false,
    currentLyf: null, attackedThisTurn: false,
    deviant: false, natural: true,
  }));

  const hasL1 = hand.some(id => { const c = ESCENDANTS.find(e => e.id === id); return c && c.level === 1; });
  if (!hasL1) {
    const fullPool = [];
    Object.entries(grimoire).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) fullPool.push(id); });
    const shuffled = [...fullPool].sort(() => Math.random() - 0.5);
    hand.length = 0;
    hand.push(...shuffled.slice(0, 5));
  }

  return {
    name: `${difficulty} NPC`,
    anima: 30, maxAnima: 50, chao: 3, maxChao: 3,
    grimoire, luxmoire,
    prophecy: starterDeck.prophecy || null,
    hand,
    field: {
      esclings,
      tomes: [null, null, null],
      curios: starterDeck.curios || [null, null],
      prophecy: starterDeck.prophecy || null,
    },
    necropolis: [],
    shadowGamblingStreak: 0,
    isNPC: true,
    difficulty,
    ...brainStamp,
    // NOTE: resurrectedCards (R44 record) is intentionally NOT seeded —
    // Firebase strips empty arrays; every reader defaults `|| []`.
  };
}

// ─────────────────────────────────────────────────────────────
// MAIN ROUTER
// ─────────────────────────────────────────────────────────────
export async function runNPCTurn(matchCode, match, difficulty) {
  const freshMatch = await getMatch(matchCode);
  if (!freshMatch || freshMatch.status === "finished") return;
  if (freshMatch.activePlayer !== NPC_IDX) return;

  const npc = freshMatch?.players?.[NPC_IDX];
  const player = freshMatch?.players?.[0];
  if (!npc || !player) return;

  const nd = difficulty
    ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase()
    : "Champion";

  // ── DEPTHS BRAIN: resolved off the seat-1 object, stamped at build
  // time — the deeper the mind, the longer it visibly considers you.
  // Purely dramatic; pacing only, never legality.
  const brain = resolveBrain(npc, nd);
  const thinkTime = Math.round(
    (THINK_TIME[nd] || THINK_TIME.Champion) * (1 + 0.35 * brain.skill)
  );
  await sleep(thinkTime);

  const currentMatch = await getMatch(matchCode);
  if (!currentMatch || currentMatch.status === "finished") return;
  if (currentMatch.activePlayer !== NPC_IDX) return;
  const freshNpc    = currentMatch?.players?.[NPC_IDX];
  const freshPlayer = currentMatch?.players?.[0];
  if (!freshNpc || !freshPlayer) return;

  try {
    if (nd === NPC_DIFFICULTIES.BEB)           await runBebTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else if (nd === NPC_DIFFICULTIES.CHAMPION) await runChampionTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else if (nd === NPC_DIFFICULTIES.LORD)     await runLordTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else                                       await runBebTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
  } catch (e) {
    console.error("[NPC] CRASH:", e.message, e.stack);
    // ── BUG #1 FIX (NPC soft-lock): crash recovery ──
    // If a turn function throws, the old code only logged it — the match
    // stayed on activePlayer: NPC_IDX forever, so the NPC "stopped playing"
    // and the human was locked out (not their turn). ALWAYS hand the turn
    // back to the player so the game can continue, even on an AI error.
    // Best-effort: reset attacked flags + clear the cycle flag, then pass.
    try {
      const recover = await getMatch(matchCode);
      if (recover && recover.status !== "finished" && recover.activePlayer === NPC_IDX) {
        const npcRec = recover?.players?.[NPC_IDX];
        if (npcRec?.field?.esclings) {
          await updatePlayer(matchCode, NPC_IDX, {
            field: { ...npcRec.field, esclings: resetEndOfTurn(npcRec.field.esclings) },
            chaoSpentThisCycle: false,
          });
        }
        await updateMatch(matchCode, {
          activePlayer: 0,
          turn: (recover.turn || 0) + 1,
        });
      }
    } catch (recoverErr) {
      console.error("[NPC] CRASH RECOVERY FAILED:", recoverErr.message);
    }
  }
}

// ═════════════════════════════════════════════════════════════
// BEB — Chaotic. Always acts. Random (but LEGAL) choices. ALWAYS
// aggressive. AWAKENING: Beb now legally Deviant-Transcends, sometimes
// Deviant-Ascends on a whim, occasionally trains a random Primera, and
// occasionally fires a soul card — chaos with a rulebook. No searches,
// no resurrection strategy: Beb doesn't plan, Beb DOES.
// DEPTHS BRAIN: skill ramps every appetite — an Escling-rank Beb barely
// remembers its own Necropolis; a top-of-band Beb is chaos that lands.
// Temperament shifts the same gates (Reckless +, Patient −, Greedy
// hoards Luxmoire, Cold stops rolling dice entirely).
// ═════════════════════════════════════════════════════════════
async function runBebTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  let hand     = [...(npc.hand || [])];
  let field    = JSON.parse(JSON.stringify(npc.field || {}));
  let bebGrimoire = { ...npc.grimoire };
  let bebLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  // Universal cost rule: no local chao/anima mirror. Every spend goes
  // through npcSpendChao / npcSpendAnima, which read fresh from Firebase.

  // 1. DRAW — always tries to draw something (was Math.random() > 0.6 gate, now removed)
  // R10 (Rulez v2): cap is 8, draw-then-discard. Beb happily draws AT the
  // cap — the end-of-turn enforcer churns the worst card to the Necropolis.
  if (hand.length <= NPC_HAND_LIMIT) {
    const fromLux = Object.keys(bebLuxmoire).length > 0
      && chance(0.5 + (brain?.t?.luxBias || 0), brain);
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? bebLuxmoire : bebGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) bebLuxmoire = newPool; else bebGrimoire = newPool;
      // Persist draw + brief pause so player sees it
      await updatePlayer(code, NPC_IDX, {
        hand,
        grimoire: bebGrimoire,
        luxmoire: bebLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // 1b. RESURRECT — v2 (caP'n ruling): a standard once-per-turn play
  // for EVERY duelist. Beb remembers the Necropolis exists sometimes —
  // rules-true when it fires, forgotten when it doesn't. DEPTHS BRAIN:
  // the memory sharpens with skill (30% at the bottom of the ladder,
  // 65% at the top of the Beb band).
  if (turn >= 3 && chance(lerp(0.3, 0.65, brain?.skill ?? 0.5), brain)) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // 2. TRANSCEND — fill all empty slots, ONE at a time (legal picks only)
  while (true) {
    const r = await placeOneEscendant(code, hand, field, false);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // 3. FLIP TOMES — one at a time. Cost paid here per Rulebook line 300.
  while (true) {
    const r = await flipOneTome(code, field, turn, hand, bebGrimoire);
    field = r.field; hand = r.hand; bebGrimoire = r.grimoire;
    if (!r.flipped) break;
  }

  // 4. PLACE TOME — one face-down (free)
  {
    const r = await placeOneTome(code, hand, field, turn, "Beb");
    hand = r.hand; field = r.field;
  }

  // 5. ACTIVATE TOMES — re-activate persistent face-up tomes only
  while (true) {
    const r = await activateOneTome(code, field, turn, "Beb", hand, bebGrimoire);
    field = r.field; hand = r.hand; bebGrimoire = r.grimoire;
    if (!r.activated) break;
  }

  // 6. ASCEND — once per turn TOTAL (Rulebook line 643).
  // No loop: even if multiple beings could ascend, the rule allows only
  // one. AWAKENING: sometimes Beb is willing to pay a Deviant fare on a
  // whim — chaos, but the rulebook's chaos. DEPTHS BRAIN: the whim
  // scales with skill (25% → 55% across the Beb band).
  {
    const r = await ascendOne(code, hand, field, false,
      chance(lerp(0.25, 0.55, brain?.skill ?? 0.5), brain));
    hand = r.hand; field = r.field;
  }

  // 7. TRAIN — AWAKENING: sometimes Beb slaps one legal Primera onto
  // something. No plan. Just vibes. DEPTHS BRAIN: 35% → 70% with skill.
  if (chance(lerp(0.35, 0.7, brain?.skill ?? 0.5), brain)) {
    const r = await trainOnePrimera(code, hand, field);
    hand = r.hand; field = r.field;
  }

  // 8. SOUL ACTIVATION — AWAKENING: sometimes Beb fires one soul card
  // if it's useful and affordable (includes the Dragon Eyes gamble —
  // Beb is the only tier that rolls those dice). DEPTHS BRAIN:
  // 20% → 50% with skill.
  if (chance(lerp(0.2, 0.5, brain?.skill ?? 0.5), brain)) {
    const r = await activateOneSoulCard(code, hand, field, bebGrimoire, turn, usedSouls, "Beb");
    hand = r.hand; field = r.field; bebGrimoire = r.grimoire;
    if (r.matchOver) return;
  }

  // 9. ATTACK — ALWAYS
  let esclings = field.esclings;
  if (turn >= 2) {
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Beb", field, turn, brain);
    if (npcDied) return; // Fate Maker / Sweet Dreams ended the match
    field.esclings = esclings;
    if (killed) {
      field.esclings = resetEndOfTurn(esclings);
      await updatePlayer(code, NPC_IDX, { hand, field, chao: npc.maxChao || 3, grimoire: bebGrimoire, luxmoire: bebLuxmoire });
      await endMatch(code, NPC_IDX);
      return;
    }
  }

  // END TURN
  // Read chaoSpentThisCycle fresh from Firebase — npcSpendChao writes it
  // to Firebase on every spend, so it's authoritative.
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = resetEndOfTurn(esclings);
  const npcFresh   = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  const bebSpent   = !!npcFresh.chaoSpentThisCycle;
  const newMaxChao = (!bebSpent && turn >= 2) ? Math.min(10, (npcFresh.maxChao || 3) + 1) : (npcFresh.maxChao || 3);
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    chao: newMaxChao, maxChao: newMaxChao,
    chaoSpentThisCycle: false,
    grimoire: bebGrimoire, luxmoire: bebLuxmoire,
  });
  await updateMatch(code, { activePlayer: 0, turn: (match.turn || 0) + 1 });
}

// ═════════════════════════════════════════════════════════════
// CHAMPION — Plays correctly. Attacks every turn. Manages Chao.
// AWAKENING: Champion now runs the fundamentals a good club player
// would — trains Primeras every turn, dresses its Natural L3 in its
// U'hlti and Affinity, fires the simple soul effects, Grimoire-
// searches an L1 when its board is empty-handed, and resurrects when
// its board has been wiped. Natural ascensions only — Deviancy is
// the Lord's (and Beb's) vice.
// DEPTHS BRAIN: the top of the Champion band studies harder — at
// skill ≥ 0.38 it tutors its ascension lines from the Grimoire (a move
// that was Lord-only), and at ≥ 0.40 it fires a third soul effect per
// turn. Greedy Champions raid the Luxmoire early; Reckless/Cold ones
// go for the throat when trained to redirect.
// ═════════════════════════════════════════════════════════════
async function runChampionTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  let hand        = [...(npc.hand || [])];
  let field       = JSON.parse(JSON.stringify(npc.field || {}));
  let champGrimoire = { ...npc.grimoire };
  let champLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  let drewThisTurn = false;
  // Universal cost rule: no local mirror — npcSpendChao reads fresh.

  // 1a. EMERGENCY GRIMOIRE SEARCH — board empty + no L1 in hand: pay 2
  // to tutor a Level 1 rather than praying at the top of the Grimoire.
  // Consumes the turn's draw, exactly like the player (AWAKENING).
  {
    const boardEmpty = (field.esclings || []).every(s => !s?.escendant);
    const l1InHand = hand.some(id => {
      const c = getCard(id);
      return c?.type === "Escendant" && c.level === 1;
    });
    if (boardEmpty && !l1InHand && turn >= 1) {
      const r = await grimoireSearchOne(code, hand, champGrimoire, 1);
      hand = r.hand; champGrimoire = r.grimoire;
      if (r.searched) drewThisTurn = true;
    }
  }

  // 1a-ii. ASCENSION-LINE SEARCH — DEPTHS BRAIN (skill ≥ 0.38): the
  // studied Champion tutors a standing being's natural next level from
  // the Grimoire instead of praying at the top of it. Same fare, same
  // draw-consumption, same helper the Lord uses — a move earned at the
  // top of the Champion band and by every gatekeeper Champion.
  if (!drewThisTurn && (brain?.skill ?? 0) >= SKILL_CHAMP_LINE_SEARCH) {
    const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
    if ((npcNow.chao ?? 0) >= 6) {
      for (const s of (field.esclings || [])) {
        const cur = getCard(s?.escendant);
        if (!cur || cur.level >= 3 || s?.fallen) continue;
        const handHasNext = hand.some(id => canAscendNatural(cur, getCard(id)));
        if (handHasNext) continue;
        const wantIds = Object.keys(champGrimoire).filter(gid =>
          (champGrimoire[gid] || 0) > 0 && canAscendNatural(cur, getCard(gid)));
        if (wantIds.length === 0) continue;
        const r = await grimoireSearchOne(code, hand, champGrimoire, cur.level + 1, wantIds);
        hand = r.hand; champGrimoire = r.grimoire;
        if (r.searched) drewThisTurn = true;
        break;
      }
    }
  }

  // 1b. SMART DRAW — Luxmoire if L2+ ready (gate removed; always evaluates)
  // R10 (Rulez v2): cap 8. Champion won't draw INTO a forced discard.
  // DEPTHS BRAIN: a Greedy Champion raids the Luxmoire as soon as it
  // holds cards — Primera hunger over curve discipline.
  if (!drewThisTurn && hand.length < NPC_HAND_LIMIT) {
    const esclings = field.esclings || [];
    const hasL2 = esclings.some(s => getCard(s?.escendant)?.level >= 2);
    const luxHasCards = Object.keys(champLuxmoire).length > 0;
    const fromLux = (hasL2 || (brain?.t?.luxBias || 0) > 0) && luxHasCards; // ── AGGRESSION BUMP ── (was random>0.4)
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? champLuxmoire : champGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) champLuxmoire = newPool; else champGrimoire = newPool;
      await updatePlayer(code, NPC_IDX, {
        hand, grimoire: champGrimoire, luxmoire: champLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // 2. RESURRECT — AWAKENING v2 (caP'n ruling, July 2026): resurrection
  // is a standard once-per-turn play, not a last resort. Champion
  // attempts one legal revival every turn (resurrectOne self-gates on
  // candidates, the R44 record, chain order, and cost).
  if (turn >= 3) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // 3. TRANSCEND — natural preferred, one at a time
  while (true) {
    const r = await placeOneEscendant(code, hand, field, true);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // 4. ASCEND — once per turn TOTAL (Rulebook line 643). Natural only.
  {
    const r = await ascendOne(code, hand, field, true, false);
    hand = r.hand; field = r.field;
  }

  // 5. TRAIN — AWAKENING: attach every legal Primera in hand, one write
  // at a time. Champion plays its support cards instead of hoarding them.
  while (true) {
    const r = await trainOnePrimera(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.trained) break;
  }

  // 6. U'HLTI / AFFINITY — AWAKENING: dress the Natural L3.
  while (true) {
    const r = await attachOneSoulCard(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.attached) break;
  }

  // 7. FLIP WAITING TOMES — cost paid here per Rulebook
  while (true) {
    const r = await flipOneTome(code, field, turn, hand, champGrimoire);
    field = r.field; hand = r.hand; champGrimoire = r.grimoire;
    if (!r.flipped) break;
  }

  // 8. PLACE TOME — when hand has 4+ (intentional management). Free.
  if (hand.length >= 4) {
    const r = await placeOneTome(code, hand, field, turn, "Champion");
    hand = r.hand; field = r.field;
  }

  // 9. ACTIVATE TOMES — re-activate persistent face-up tomes only
  while (true) {
    const r = await activateOneTome(code, field, turn, "Champion", hand, champGrimoire);
    field = r.field; hand = r.hand; champGrimoire = r.grimoire;
    if (!r.activated) break;
  }

  // 10. SOUL ACTIVATIONS — AWAKENING: the simple whitelist (Intangible,
  // Nullify, dims, cheap draws). Two firings per turn; three at the top
  // of the band (DEPTHS BRAIN, skill ≥ 0.40).
  const champSoulCap = (brain?.skill ?? 0) >= SKILL_CHAMP_SOUL3 ? 3 : 2;
  for (let sa = 0; sa < champSoulCap; sa++) {
    const r = await activateOneSoulCard(code, hand, field, champGrimoire, turn, usedSouls, "Champion");
    hand = r.hand; field = r.field; champGrimoire = r.grimoire;
    if (r.matchOver) return;
    if (!r.activated) break;
  }

  // 11. ATTACK
  let esclings = field.esclings;
  if (turn >= 2) {
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Champion", field, turn, brain);
    if (npcDied) return; // Fate Maker / Sweet Dreams ended the match
    field.esclings = esclings;
    if (killed) {
      field.esclings = resetEndOfTurn(esclings);
      await updatePlayer(code, NPC_IDX, { hand, field, chao: npc.maxChao || 3, grimoire: champGrimoire, luxmoire: champLuxmoire });
      await endMatch(code, NPC_IDX);
      return;
    }
  }

  // END TURN — read chaoSpentThisCycle fresh from Firebase
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = resetEndOfTurn(esclings);
  const npcFresh    = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  const champSpent  = !!npcFresh.chaoSpentThisCycle;
  const newMaxChao  = (!champSpent && turn >= 2) ? Math.min(10, (npcFresh.maxChao || 3) + 1) : (npcFresh.maxChao || 3);
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    chao: newMaxChao, maxChao: newMaxChao,
    chaoSpentThisCycle: false,
    grimoire: champGrimoire, luxmoire: champLuxmoire,
  });
  await updateMatch(code, { activePlayer: 0, turn: (match.turn || 0) + 1 });
}

// ─────────────────────────────────────────────────────────────
// DEPTHS BRAIN: PROPHECY HELPERS — compact mirrors of
// prophecyEngine's checkers (isSupreme, divine titles, Ilk sweeps).
// The engine itself watches BOTH seats on every state update and ends
// the match the moment condition + fare are met — the NPC never
// "declares" anything. These helpers only let the Lord PLAY TOWARD
// that moment instead of stumbling into it.
// ─────────────────────────────────────────────────────────────
const DIVINE_AFFS = ["aff_evil", "aff_holy"]; // D'aeternuz / A'eternuz
const ILK_PROPHECIES = {
  proph_space_race:      "Space",
  proph_phantom_planet:  "Phantom",
  proph_dragonforce:     "Dragon",
  proph_time_to_pretend: "Time",
};
// Prophecy fare is 15 Chao (getProphecyChaoCost default; Ace's 13 is
// Shadow-Gambling-only — never Lord-actionable). The engine checks the
// pool itself — the Lord just has to let the pool GROW.

function soulPieces(slot) {
  const soul = (slot?.soul || []).map(id => getCard(id)).filter(Boolean);
  return {
    primeraLevels: new Set(soul.filter(c => c.type === "Primera").map(c => c.level)),
    hasUhlti: soul.some(c => c.type === "U'hlti"),
    affinityId: (soul.find(c => c.type === "Affinity") || {}).id || null,
  };
}

// Mirror of prophecyEngine.isSupreme: Natural L3 + Primera L1/L2/L3 +
// U'hlti + Affinity, standing (not Fallen).
function isSupremeSlot(slot) {
  if (!slot?.escendant || slot.fallen || !slot.natural) return false;
  const c = getCard(slot.escendant);
  if (!c || c.level !== 3) return false;
  const p = soulPieces(slot);
  return p.primeraLevels.has(1) && p.primeraLevels.has(2)
    && p.primeraLevels.has(3) && p.hasUhlti && !!p.affinityId;
}

// Assess the NPC's OWN prophecy: is it worth playing toward, is the
// board condition already met (→ hold Chao and let it regrow to the
// fare), and which Luxmoire pieces would advance it?
// Returns null when the prophecy is not Lord-actionable:
//   • tablet_nullus — the OPPONENT builds it; nothing to push
//   • ace_of_shades — Shadow Gambling streaks; Beb's dice, not chess
//   • supreme-based prophecies with < 2 distinct U'hltis or no divine
//     Affinity anywhere in the NPC's world (every 2026 starter deck
//     ships exactly ONE U'hlti — those go dormant here by design, and
//     the check future-proofs richer NPC decks for free)
function assessProphecyPush(npcLive, playerSnap, field, hand, grimoire, luxmoire) {
  const pid = npcLive?.prophecy || field?.prophecy || null;
  if (!pid) return null;

  // Bloody Hell — 45+ total Anima lost by EITHER duelist, fare 15.
  // The Lord's most reachable prophecy: it deals the blood itself.
  if (pid === "proph_bloody_hell") {
    const worst = Math.max(playerSnap?.totalAnimaLost || 0, npcLive?.totalAnimaLost || 0);
    return { active: true, kind: "blood", boardMet: worst >= 45, near: worst >= 28, wantSoulPieceIds: [] };
  }

  const ilk = ILK_PROPHECIES[pid] ?? (pid === "proph_tablet_omnis" ? null : undefined);
  if (ilk === undefined) return null;

  const everywhere = [
    ...hand,
    ...Object.keys(grimoire || {}),
    ...Object.keys(luxmoire || {}),
    ...((field.esclings || []).flatMap(s => s?.soul || [])),
  ];
  const uhltiIds = new Set(everywhere.filter(id => getCard(id)?.type === "U'hlti"));
  const hasDivine = everywhere.some(id => DIVINE_AFFS.includes(id));
  if (uhltiIds.size < 2 || !hasDivine) return null; // unreachable — dormant

  const cands = (field.esclings || []).filter(s => {
    if (!s?.escendant || s.fallen || !s.natural) return false;
    const c = getCard(s.escendant);
    return c && (ilk === null || c.ilk === ilk);
  });
  const l3cands  = cands.filter(s => getCard(s.escendant)?.level === 3);
  const supremes = cands.filter(isSupremeSlot);
  const divineWorn = supremes.some(s => DIVINE_AFFS.includes(soulPieces(s).affinityId));
  const boardMet = supremes.length >= 2 && divineWorn;

  // Missing Supreme pieces the Luxmoire could supply right now.
  const want = [];
  l3cands.forEach(s => {
    const p = soulPieces(s);
    if (!p.hasUhlti) {
      Object.keys(luxmoire || {}).forEach(id => {
        const c = getCard(id);
        if (c?.type === "U'hlti" && c.owner === s.escendant && (luxmoire[id] || 0) > 0) want.push(id);
      });
    } else if (!p.affinityId) {
      DIVINE_AFFS.forEach(id => { if ((luxmoire || {})[id] > 0) want.push(id); });
    }
  });

  return { active: true, kind: "supreme", boardMet, near: l3cands.length >= 2, wantSoulPieceIds: want };
}

// PROPHECY DENIAL (skill ≥ 0.95): find the PLAYER being whose ruin
// most sets back THEIR prophecy — the deepest-souled Natural L3 of
// their prophecy's Ilk, once their Chao pool says the fare is coming.
// Returns a player slot index, or -1 when there's nothing to deny.
function findDenialTarget(playerFresh) {
  const pid = playerFresh?.prophecy;
  const ilk = ILK_PROPHECIES[pid] ?? (pid === "proph_tablet_omnis" ? null : undefined);
  if (ilk === undefined) return -1;
  if ((playerFresh?.chao || 0) < 8) return -1; // no fare in sight — no panic
  let best = -1, bestScore = 2; // require real progress: ≥ 3 soul pieces
  ((playerFresh.field?.esclings) || []).forEach((s, i) => {
    if (!s?.escendant || s.fallen || !s.natural) return;
    const c = getCard(s.escendant);
    if (!c || c.level !== 3) return;
    if (ilk !== null && c.ilk !== ilk) return;
    const p = soulPieces(s);
    const score = p.primeraLevels.size + (p.hasUhlti ? 1 : 0) + (p.affinityId ? 1 : 0);
    if (score > bestScore) { best = i; bestScore = score; }
  });
  return best;
}

// ═════════════════════════════════════════════════════════════
// LORD — Chess-level: reads board, calculates kill turns, pivots.
// AWAKENING: the Lord now wields the full arsenal —
//   • Grimoire Search to complete ascension lines and to refill an
//     empty board (never top-decking when it can tutor).
//   • Luxmoire Search to pull the exact trainable Primera it wants.
//   • Deviant Ascends when no natural line exists and the fare is
//     worth the tempo.
//   • Full training + U'hlti + Affinity — the Lord will assemble a
//     Supreme Being if the duel runs long enough.
//   • Soul activations across the whole dispatcher, value-gated.
//   • Rules-true resurrection — including the L3-over-standing-L2
//     chain revive, the single biggest tempo swing it owns.
// The old sections 11/12 (Fallen bypass + kill-shot tunnel vision)
// collapsed INTO doAttacks: animaClear() already encodes the R26
// Fallen bypass, the Lord's no-plan branch already strikes Anima when
// clear, and collapsing them means defensive states (Fate Maker,
// Sweet Dreams, Intangible) apply during kill shots too — they used
// to be bypassed by the raw writes. One attack phase, one rulebook.
// DEPTHS BRAIN — the Lord's skill sub-bands:
//   ≥ 0.70 PROPHECY: the Lord races its own win condition. Blood
//          prophecies escalate it to AGGRO early, and a met condition
//          coils it around its Chao pool — no optional spend, natural
//          +1 growth carries it to the fare, the prophecyEngine ends
//          the duel. A second win-axis the player has to watch.
//   ≥ 0.82 LETHAL FOCUS: a clear, lethal Anima lane is never traded
//          for a being kill — see doAttacks.
//   ≥ 0.95 DENIAL: hunts the being that completes the PLAYER's
//          prophecy — see doAttacks.
// ═════════════════════════════════════════════════════════════
async function runLordTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  let hand        = [...(npc.hand || [])];
  let field       = JSON.parse(JSON.stringify(npc.field || {}));
  let lordGrimoire = { ...npc.grimoire };
  let lordLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  let drewThisTurn = false;
  // Universal cost rule: no local chao/anima mirror. Every spend goes
  // through npcSpendChao / npcSpendAnima — fresh Firebase reads each
  // time. The PLANNING values below (npc.chao, npc.anima) are the
  // turn-start snapshot, fine for planning since no spends have
  // happened yet at that point.
  const planChao  = npc.chao ?? (npc.maxChao || 3);
  const planAnima = npc.anima ?? 30;

  // ── DEEP BOARD ASSESSMENT (AWAKENING: effective values) ──
  const myStanding      = standingBeings(field);
  const myTotalLyt      = totalAvailableLyt(field, player.field);
  const playerStanding  = standingBeings(player.field || {});
  const playerFallen    = ((player.field?.esclings) || []).filter(s => s?.escendant && s.fallen);
  const playerThreat    = threatScore(player, player.field);
  const turnsToMyDeath  = turnsUntilDeath(npc.anima || 30, player.field, field);
  const playerAnima     = player.anima || 30;
  const playerAnimaClearNow = playerAnimaClear(player.field);

  const canKillNow = playerAnimaClearNow && myTotalLyt >= playerAnima;
  const canKillNextTurn = myTotalLyt >= playerAnima && playerStanding.length <= 1;

  // Resurrection intel (planning snapshot; resurrectOne re-reads fresh)
  const revivedSnap = npc.resurrectedCards || [];
  const necUnrevived = (npc.necropolis || []).filter(id => {
    if (revivedSnap.includes(id)) return false;
    return getCard(id)?.type === "Escendant";
  });
  const shouldResurrect = necUnrevived.length > 0
    && myStanding.length === 0
    && turn >= 3;

  const grimoireEmpty = Object.keys(lordGrimoire).length === 0;
  const handEmpty = hand.length === 0;
  const shouldGraveshift = grimoireEmpty && handEmpty && planAnima > 12;

  const baseMode = canKillNow     ? "KILL"
    : turnsToMyDeath <= 1         ? "SURVIVAL"
    : canKillNextTurn             ? "AGGRO"
    : playerThreat >= 40          ? "AGGRO"
    : shouldResurrect             ? "RESURRECT"
    : "BALANCE";

  // ── PROPHECY BRAIN (DEPTHS BRAIN, skill ≥ 0.70) ──
  const prophecyPush = (brain?.skill ?? 0) >= SKILL_PROPHECY
    ? assessProphecyPush(npc, player, field, hand, lordGrimoire, lordLuxmoire)
    : null;
  // A met condition makes every optional Chao spend a delay: Chao grows
  // +1 per no-spend cycle, and the prophecyEngine ends the duel the
  // moment the pool reaches the fare (15). KILL outranks it (dimming
  // the Anima to 0 this turn is faster than any prophecy) and SURVIVAL
  // outranks it (a dead Lord collects no fare).
  const holdChao = !!(prophecyPush?.boardMet)
    && baseMode !== "KILL" && baseMode !== "SURVIVAL";
  // Nearness turns the screws — a BALANCE Lord with blood in the water
  // (or two L3 candidates standing) escalates to AGGRO on its own.
  const mode = (prophecyPush?.near && baseMode === "BALANCE") ? "AGGRO" : baseMode;

  // ── 1. TURN 1 OPTIMAL SETUP — placement only, still sequential ──
  if (turn === 0) {
    while (true) {
      const r = await placeOneEscendant(code, hand, field, true);
      hand = r.hand; field = r.field;
      if (!r.placed) break;
    }
    // No tome on turn 0 (rule)
    field.esclings = resetEndOfTurn(field.esclings);
    await updatePlayer(code, NPC_IDX, {
      hand, field,
      chao: npc.maxChao || 3, maxChao: npc.maxChao || 3,
      chaoSpentThisCycle: false,
      grimoire: lordGrimoire, luxmoire: lordLuxmoire,
    });
    await updateMatch(code, { activePlayer: 0, turn: 1 });
    return;
  }

  // ── 2. GRAVESHIFT — pay 10 anima via canonical helper ──
  if (shouldGraveshift) {
    const paid = await npcSpendAnima(code, 10);
    if (paid) {
      const necNow = ((await getMatch(code))?.players?.[NPC_IDX]?.necropolis) || [];
      const newGrimoire = {};
      [...hand, ...necNow].forEach(id => {
        newGrimoire[id] = (newGrimoire[id] || 0) + 1;
      });
      hand = [];
      await updatePlayer(code, NPC_IDX, {
        grimoire: newGrimoire,
        necropolis: [],
        hand: [],
      });
      lordGrimoire = newGrimoire;
      await sleep(ACTION_DELAY);
    }
  }

  // ── 3. RESURRECTION — rules-true (R44-R48), reads fresh state ──
  // v2 (caP'n ruling, July 2026): a standard once-per-turn play. The
  // Lord attempts one revival every turn — chain revives (an L3 back
  // over its standing L2, full soul intact) are the jackpot, and
  // resurrectOne's highest-level-first ordering already grabs them.
  // PROPHECY BRAIN: the fare is Chao-else-Anima — skipped while the
  // Lord is holding its pool for the prophecy fare.
  if (turn >= 3 && !holdChao) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // ── 4. SMART DRAW (with hand cap discard) ──
  if (hand.length >= 7) {
    const fieldBeings = new Set((field.esclings || []).map(s => s?.escendant).filter(Boolean));
    const worstIdx = (() => {
      for (let i = 0; i < hand.length; i++) {
        const c = getCard(hand[i]);
        if (c?.type === "Escendant" && c?.level === 1 && fieldBeings.has(hand[i])) return i;
      }
      const dupeIdx = hand.findIndex((id, i) => getCard(id)?.type === "Tome" && hand.slice(0, i).includes(id));
      if (dupeIdx !== -1) return dupeIdx;
      return hand.findIndex(id => getCard(id)?.type === "Tome");
    })();
    if (worstIdx !== -1) {
      hand.splice(worstIdx, 1);
    }
  }

  // ── 4a. EMERGENCY BEING SEARCH (AWAKENING) — empty slots, no L1 in
  // hand, L1 in Grimoire: pay 2 to tutor rather than pray. Consumes
  // the turn's draw, exactly like the player's Grimoire Search.
  // PROPHECY BRAIN: skipped while holding (2 Chao resets the growth).
  if (!holdChao) {
    const emptySlot = (field.esclings || []).some(s => !s?.escendant);
    const l1InHand = hand.some(id => {
      const c = getCard(id);
      return c?.type === "Escendant" && c.level === 1;
    });
    if (emptySlot && !l1InHand) {
      const r = await grimoireSearchOne(code, hand, lordGrimoire, 1);
      hand = r.hand; lordGrimoire = r.grimoire;
      if (r.searched) drewThisTurn = true;
    }
  }

  // ── 4b. ASCENSION-LINE SEARCH (AWAKENING) — a standing being's
  // natural next level sits in the Grimoire but not in hand: pay 4 to
  // complete the line. Chao-gated so tomes still get fed; skipped in
  // SURVIVAL/RESURRECT where every point matters elsewhere, and while
  // holding the pool for a prophecy fare.
  if (!drewThisTurn && mode !== "SURVIVAL" && mode !== "RESURRECT" && !holdChao) {
    const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
    if ((npcNow.chao ?? 0) >= 6) {
      for (const s of (field.esclings || [])) {
        const cur = getCard(s?.escendant);
        if (!cur || cur.level >= 3 || s?.fallen) continue;
        const handHasNext = hand.some(id => canAscendNatural(cur, getCard(id)));
        if (handHasNext) continue;
        const wantIds = Object.keys(lordGrimoire).filter(gid =>
          (lordGrimoire[gid] || 0) > 0 && canAscendNatural(cur, getCard(gid)));
        if (wantIds.length === 0) continue;
        const r = await grimoireSearchOne(code, hand, lordGrimoire, cur.level + 1, wantIds);
        hand = r.hand; lordGrimoire = r.grimoire;
        if (r.searched) drewThisTurn = true;
        break;
      }
    }
  }

  // ── 4c. LUXMOIRE SEARCH (AWAKENING v3) — 5 Chao to tutor the exact
  // trainable Primera it wants. CANON (July 4): the search CONSUMES the
  // turn's draw, same as Grimoire Search — so it now sits BEFORE the
  // normal draw and competes with it. Only when Chao-rich, nothing
  // trainable already in hand, and the duel isn't in crisis.
  if (!drewThisTurn && mode !== "SURVIVAL" && mode !== "RESURRECT" && !holdChao) {
    // PROPHECY BRAIN: while a supreme push is live and unmet, the
    // search also hunts the missing Supreme pieces (the exact owner-
    // bound U'hlti for a standing L3, or a divine Affinity once the
    // U'hlti is worn). Legal parity: the PLAYER's Luxmoire Search
    // modal offers EVERY Luxmoire card, not just Primeras — the old
    // Primera-only restriction here was NPC conservatism, not a rule.
    const wantPieces = (prophecyPush?.kind === "supreme" && !prophecyPush.boardMet)
      ? (prophecyPush.wantSoulPieceIds || []).filter(id => !hand.includes(id))
      : [];
    const handTrainable = hand.some(id => {
      const card = getCard(id);
      if (card?.type !== "Primera") return false;
      return (field.esclings || []).some(slot => {
        if (!slot?.escendant) return false;
        const being = getCard(slot.escendant);
        if (!being) return false;
        if (card.class !== being.class || card.ilk !== being.ilk) return false;
        if ((card.level || 1) > (being.level || 1)) return false;
        return !(slot.soul || []).some(sid => {
          const sc = getCard(sid);
          return sc?.type === "Primera" && sc?.level === card.level;
        });
      });
    });
    if (!handTrainable || wantPieces.length > 0) {
      const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
      if ((npcNow.chao ?? 0) >= 7) {
        const r = await luxmoireSearchOne(code, hand, field, lordLuxmoire, wantPieces);
        hand = r.hand; lordLuxmoire = r.luxmoire;
        if (r.searched) drewThisTurn = true; // CANON: search = the draw
      }
    }
  }

  // ── 4d. NORMAL DRAW — only if no search consumed it ──
  // R10 (Rulez v2): cap 8. The Lord won't draw INTO a forced discard.
  if (!drewThisTurn && hand.length < NPC_HAND_LIMIT) {
    const esclings = field.esclings || [];
    const hasL2Plus = esclings.some(s => getCard(s?.escendant)?.level >= 2);
    const luxHasCards = Object.keys(lordLuxmoire).length > 0;
    const needsAscend = esclings.some(s => canAscendFromPool(s, lordGrimoire));
    const needsPrimera = hasL2Plus && luxHasCards;
    const fromLux = needsPrimera && !needsAscend && mode !== "SURVIVAL" && mode !== "RESURRECT";
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? lordLuxmoire : lordGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) lordLuxmoire = newPool; else lordGrimoire = newPool;
      await updatePlayer(code, NPC_IDX, {
        hand, grimoire: lordGrimoire, luxmoire: lordLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // ── 5. TRANSCEND — natural, one at a time ──
  while (true) {
    const r = await placeOneEscendant(code, hand, field, true);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // ── 6. ASCEND — once per turn TOTAL (Rulebook line 643). ──
  // AWAKENING: Deviancy unlocked — when no natural line exists, the
  // Lord pays the Deviant fare (deviantAscendCost) to keep climbing.
  // PROPHECY BRAIN: a supreme push forbids Deviant ascends entirely —
  // Deviants can never wear a U'hlti (R64), so tainting a line would
  // sabotage the very win the Lord is building. A Chao hold forbids
  // them too (the fare resets the growth).
  {
    const allowDeviant = !holdChao && prophecyPush?.kind !== "supreme";
    const r = await ascendOne(code, hand, field, true, allowDeviant);
    hand = r.hand; field = r.field;
  }

  // ── 6a. TRAIN — every legal Primera in hand, one write at a time ──
  while (true) {
    const r = await trainOnePrimera(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.trained) break;
  }

  // ── 6b. U'HLTI / AFFINITY — dress the Natural L3 (Supreme road) ──
  while (true) {
    const r = await attachOneSoulCard(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.attached) break;
  }

  // ── 7. FLIP WAITING TOMES — cost paid here per Rulebook ──
  // PROPHECY BRAIN: flips pay Chao — held back while holding. The
  // tomes wait one turn; the win doesn't.
  while (!holdChao) {
    const r = await flipOneTome(code, field, turn, hand, lordGrimoire);
    field = r.field; hand = r.hand; lordGrimoire = r.grimoire;
    if (!r.flipped) break;
  }

  // ── 8. TOME PLACEMENT — strategic, ONE per call. Loop fills empties. ──
  const lordChooserMode =
    mode === "SURVIVAL" ? "Lord-survival"
    : (mode === "AGGRO" || mode === "KILL") ? "Lord-aggro"
    : "Lord-balance";
  while (true) {
    const r = await placeOneTome(code, hand, field, turn, lordChooserMode);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // ── 9. TOME ACTIVATION — re-activate persistent face-up tomes ──
  // PROPHECY BRAIN: activations pay fares — silenced while holding.
  while (!holdChao) {
    const r = await activateOneTome(code, field, turn, "Lord", hand, lordGrimoire);
    field = r.field; hand = r.hand; lordGrimoire = r.grimoire;
    if (!r.activated) break;
  }

  // ── 9a. SOUL ACTIVATIONS (AWAKENING) — the full dispatcher, value-
  // gated per effect. Loops until nothing useful/affordable remains
  // (capped at 4 firings a turn so the Chao pool still feeds combat).
  // PROPHECY BRAIN: soul fares are optional spends — held back too.
  for (let sa = 0; sa < (holdChao ? 0 : 4); sa++) {
    const r = await activateOneSoulCard(code, hand, field, lordGrimoire, turn, usedSouls, "Lord");
    hand = r.hand; field = r.field; lordGrimoire = r.grimoire;
    if (r.matchOver) return;
    if (!r.activated) break;
  }

  // ── 10. OFFENSIVE TOME DESTRUCTION ──
  // ── AGGRESSION BUMP ── removed `mode !== "SURVIVAL"` gate.
  // PROPHECY BRAIN: pays a fare — skipped while holding.
  if (!holdChao) {
    const fp = (await getMatch(code))?.players?.[0] || player;
    const fpTomes = riftArray(fp.field?.tomes, 3);
    const playerTomes = fpTomes.filter(t => t?.id && !t.faceDown);
    if (playerTomes.length > 0) {
      const destructTome = hand.find(id => ["tome_nothing_left", "tome_regnant", "tome_lyt_hearted"].includes(id));
      if (destructTome) {
        const destructCard = getCard(destructTome);
        const cost      = destructCard?.chaoCost  || 3;
        const animaCost = destructCard?.animaCost || 0;

        // Pre-check both costs against fresh Firebase before spending either
        const m = await getMatch(code);
        const npcLive = m?.players?.[NPC_IDX] || {};
        const canAfford = (npcLive.chao ?? 0) >= cost
                       && (npcLive.anima ?? 0) >= animaCost;

        if (canAfford && (await npcSpendChao(code, cost)) && (await npcSpendAnima(code, animaCost))) {
          hand.splice(hand.indexOf(destructTome), 1);
          // Priority target: the D'uhmdumn taunt if one stands (must-ruin-
          // first applies to tome effects too), else the first face-up tome.
          const mrf = getRuinFirstTome(fp.field);
          const targetTomeIdx = mrf ? mrf.slotIdx : fpTomes.findIndex(t => t?.id && !t.faceDown);
          if (targetTomeIdx !== -1) {
            // AWAKENING: routed through the shared soak-aware helper
            // (identical semantics — nullifyFirstRuin consumed, ruined
            // tome to the player's Necropolis, never vanished).
            await ruinPlayerTomeSlot(code, targetTomeIdx);
          }
          await updatePlayer(code, NPC_IDX, { hand });
          await sleep(TOME_INTERRUPT);
        }
      }
    }
  }

  // ── 11. ATTACK — one phase, one rulebook (AWAKENING) ──
  // The old Fallen-bypass (11) and kill-shot (12) sections collapsed
  // into doAttacks: animaClear() encodes the R26 bypass, the Lord
  // branch strikes Anima whenever the lane is clear (that IS the kill
  // shot), and every strike now resolves defensive states — Fate
  // Maker, Sweet Dreams, and Intangible apply during lethal pushes
  // too, exactly as they do against a human.
  let esclings = field.esclings || [];
  if (turn >= 2) {
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Lord", field, turn, brain);
    if (npcDied) return; // the player's defenses won the duel mid-phase
    field.esclings = esclings;
    if (killed) {
      field.esclings = resetEndOfTurn(esclings);
      await updatePlayer(code, NPC_IDX, { hand, field, chao: npc.maxChao || 3, grimoire: lordGrimoire, luxmoire: lordLuxmoire });
      await endMatch(code, NPC_IDX);
      return;
    }
  }

  // ── 12. END TURN — read chaoSpentThisCycle fresh from Firebase ──
  // Note: anima is NOT written here. All anima changes during the turn
  // were committed via npcSpendAnima / dimNpcAnima — Firebase already
  // has the truth.
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = resetEndOfTurn(esclings);
  const npcFresh   = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  const lordSpent  = !!npcFresh.chaoSpentThisCycle;
  const newMaxChao = (!lordSpent && turn >= 2) ? Math.min(10, (npcFresh.maxChao || 3) + 1) : (npcFresh.maxChao || 3);
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    chao: newMaxChao, maxChao: newMaxChao,
    chaoSpentThisCycle: false,
    grimoire: lordGrimoire, luxmoire: lordLuxmoire,
  });
  await updateMatch(code, { activePlayer: 0, turn: (match.turn || 0) + 1 });
}
